import React, { useState, useEffect } from "react";import { loadPayments, savePayments, addPayment } from "./payments";
import AccountDetails from "./AccountDetails";
import Calculators from "./Calculators";
import Amortization from "./Amortization";
import Advisor from "./Advisor";
import Calendar from "./Calendar";
import PayoffStrategy from "./PayoffStrategy";
import Milestones, { MilestoneBanner, calculateRewards } from "./Rewards";
import Auth from "./Auth";
import Landing from "./Landing";
import Welcome from "./Welcome";
import Privacy from "./Privacy";
import Terms from "./Terms";
import { ProGate, UpgradeModal } from "./ProGate";
import { initAnalytics, identify, track } from "./analytics";
import { useRevenueCat } from "./useRevenueCat";
import { requestNotificationPermission, rescheduleAllNotifications } from "./notifications";
import { AiInsightCards } from "./AiInsights";
import { supabase } from "./supabaseClient";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { fetchAccounts, fetchPayments, fetchSettings, saveAccount, savePayment, deleteAccount, deletePayment, saveSettings } from "./supabaseSync";

const COLORS = ["#4F7FFA", "#F97316", "#10B981", "#A855F7", "#EF4444", "#F59E0B"];

// Small tappable "i" info bubble. Tap toggles a popover; stops propagation so it
// never triggers the parent card's own onClick (e.g. the tap-to-cycle cards).
function InfoBubble({ text }) {
  const [open, setOpen] = React.useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <span
        role="button"
        aria-label="More information"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 15, height: 15, borderRadius: "50%", boxSizing: "border-box",
          border: "1.5px solid #B0B8C4", color: "#8A93A2",
          fontSize: 10, fontWeight: 700, lineHeight: 1, fontStyle: "normal",
          cursor: "pointer", userSelect: "none", flexShrink: 0,
        }}
      >i</span>
      {open && (
        <>
          {/* click-away layer */}
          <span
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 998 }}
          />
          <span
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", top: "calc(100% + 8px)", left: 0,
              width: 220, maxWidth: "70vw", background: "#1a1a2e", color: "#fff",
              fontSize: 12, fontWeight: 500, lineHeight: 1.45,
              textTransform: "none", letterSpacing: 0, whiteSpace: "normal",
              padding: "10px 12px", borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 999,
            }}
          >{text}</span>
        </>
      )}
    </span>
  );
}

// Hook to detect mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 640);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

const emptyForm = { name: "", type: "Credit Card", balance: "", rate: "", minPayment: "", paymentDate: "", lastPaymentDate: "" };
const DEBT_TYPES = ["Credit Card", "Student Loan", "Auto Loan", "Personal Loan", "Medical Debt", "Mortgage", "Other"];

function loadAccounts() {
  try {
    const saved = localStorage.getItem("elde_accounts");
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return []; // New users start with no accounts — sample data removed
}
function loadPlanSettings() {
  try {
    const saved = localStorage.getItem("elde_plan");
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return { strategy: "avalanche", extraBudget: 0 };
}

function loadCustomOrder() {
  try { return JSON.parse(localStorage.getItem("elde_custom_order")) || []; } catch { return []; }
}

function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}


function getDaysBetween(fromDate, toDate) {
  if (!fromDate) return 30;
  const from = new Date(fromDate + "T00:00:00");
  const to = new Date(toDate + "T00:00:00");
  return Math.max(Math.ceil((to - from) / (1000 * 60 * 60 * 24)), 1);
}

function calcSplit(amount, balance, rate, days) {
  const interest = parseFloat(Math.min(balance * (rate / 100 / 365) * days, amount).toFixed(2));
  const principal = parseFloat(Math.max(amount - interest, 0).toFixed(2));
  return { interest, principal };
}

function loadMonthlyPayments() {
  try {
    const saved = localStorage.getItem("elde_monthly_payments");
    if (saved) {
      const data = JSON.parse(saved);
      // Reset if it's a new month
      if (data.monthKey !== getCurrentMonthKey()) return { monthKey: getCurrentMonthKey(), paid: {} };
      return data;
    }
  } catch (e) {}
  return { monthKey: getCurrentMonthKey(), paid: {} };
}

function getPaymentDay(paymentDate) {
  if (!paymentDate) return null;
  if (typeof paymentDate === "number") return paymentDate;
  if (paymentDate.includes("-")) return new Date(paymentDate + "T00:00:00").getDate();
  return parseInt(paymentDate);
}

function getDaysUntilDue(paymentDate) {
  if (!paymentDate) return null;
  const today = new Date();
  let due;
  if (typeof paymentDate === "string" && paymentDate.includes("-")) {
    due = new Date(paymentDate + "T00:00:00");
  } else {
    const day = getPaymentDay(paymentDate);
    if (!day) return null;
    due = new Date(today.getFullYear(), today.getMonth(), day);
    if (due < today) due.setMonth(due.getMonth() + 1);
  }
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function getNextDue(paymentDate, lastPaymentDate) {
  if (!paymentDate) return null;
  const today = new Date();
  // If full date string, use it directly
  if (typeof paymentDate === "string" && paymentDate.includes("-")) {
    return new Date(paymentDate + "T00:00:00");
  }
  const day = getPaymentDay(paymentDate);
  if (!day) return null;
  let due;
  if (lastPaymentDate) {
    const last = new Date(lastPaymentDate + "T00:00:00");
    due = new Date(last.getFullYear(), last.getMonth() + 1, day);
    if (due < today) due = new Date(due.getFullYear(), due.getMonth() + 1, day);
  } else {
    due = new Date(today.getFullYear(), today.getMonth(), day);
    if (due < today) due = new Date(today.getFullYear(), today.getMonth() + 1, day);
  }
  return due;
}

function getDueBadge(days) {
  if (days === null) return null;
  if (days === 0) return { label: "Due today!", color: "#EF4444", bg: "#FEE2E2" };
  if (days <= 3) return { label: `Due in ${days} day${days === 1 ? "" : "s"}`, color: "#F97316", bg: "#FFF7ED" };
  if (days <= 7) return { label: `Due in ${days} days`, color: "#F59E0B", bg: "#FFFBEB" };
  return { label: `Due in ${days} days`, color: "#10B981", bg: "#F0FDF4" };
}


function buildPaymentPlan(accounts, strategy, extraBudget, customOrder = []) {
  if (!accounts.length) return [];
  let ordered;
  if (strategy === "avalanche") ordered = [...accounts].sort((a, b) => b.rate - a.rate);
  else if (strategy === "snowball") ordered = [...accounts].sort((a, b) => a.balance - b.balance);
  else if (strategy === "custom" && customOrder.length) {
    ordered = [...customOrder.map(id => accounts.find(a => a.id === id)).filter(Boolean),
      ...accounts.filter(a => !customOrder.includes(a.id))];
  } else ordered = [...accounts];
  const focusDebt = ordered.find((a) => a.balance > 0);
  return accounts.map((acct) => {
    const isFocus = focusDebt && acct.id === focusDebt.id;
    const extra = isFocus ? extraBudget : 0;
    const rank = ordered.findIndex((a) => a.id === acct.id) + 1;
    return { ...acct, totalPayment: acct.minPayment + extra, extra, isFocus, rank };
  });
}

function calcSavings(accounts, strategy, extraBudget, customOrder = []) {
  if (!accounts.length || extraBudget <= 0) return null;

  function runSim(extra) {
    let ordered;
    if (strategy === "avalanche") ordered = [...accounts].sort((a, b) => b.rate - a.rate);
    else if (strategy === "snowball") ordered = [...accounts].sort((a, b) => a.balance - b.balance);
    else if (strategy === "custom" && customOrder.length) {
      ordered = [...customOrder.map(id => accounts.find(a => a.id === id)).filter(Boolean),
        ...accounts.filter(a => !customOrder.includes(a.id))];
    } else ordered = [...accounts];

    let debts = ordered.map((a) => ({ ...a, remaining: a.balance }));
    const totalMin = debts.reduce((s, d) => s + d.minPayment, 0);
    let budget = totalMin + extra;
    let month = 0;
    let totalInterest = 0;

    while (debts.some((d) => d.remaining > 0) && month < 600) {
      month++;
      let budgetLeft = budget;

      for (let d of debts) {
        if (d.remaining <= 0) continue;
        const interest = d.remaining * (d.rate / 100 / 12);
        totalInterest += interest;
        const pay = Math.min(d.minPayment, d.remaining + interest);
        d.remaining = Math.max(d.remaining - (pay - interest), 0);
        budgetLeft -= pay;
      }
      for (let d of debts) {
        if (d.remaining <= 0 || budgetLeft <= 0) continue;
        const extra2 = Math.min(budgetLeft, d.remaining);
        d.remaining = Math.max(d.remaining - extra2, 0);
        budgetLeft -= extra2;
      }
    }
    return { months: month, totalInterest };
  }

  const withExtra = runSim(extraBudget);
  const withMin = runSim(0);

  return {
    interestSaved: Math.max(withMin.totalInterest - withExtra.totalInterest, 0),
    monthsSaved: Math.max(withMin.months - withExtra.months, 0),
    payoffMonthsWithExtra: withExtra.months,
    payoffMonthsWithMin: withMin.months,
  };
}

function calcExtraPaymentImpact(balance, rate, minPayment) {
  const extra = Math.round(minPayment * 0.1);
  if (extra <= 0 || balance <= 0 || rate <= 0) return null;

  const r = rate / 100 / 12;
  if (minPayment <= balance * r) return null;

  function getMonths(payment) {
    if (payment <= balance * r) return null;
    return Math.ceil(Math.log(payment / (payment - balance * r)) / Math.log(1 + r));
  }

  const baseMonths = getMonths(minPayment);
  const extraMonths = getMonths(minPayment + extra);
  if (!baseMonths || !extraMonths) return null;

  const interestSaved = (baseMonths * minPayment - balance) - (extraMonths * (minPayment + extra) - balance);
  const monthsSaved = baseMonths - extraMonths;

  if (interestSaved <= 0 || monthsSaved <= 0) return null;
  return { extra, interestSaved, monthsSaved };
}

function NavHeader({ screen, setScreen, hideBalances, setHideBalances, accounts, openAddModal, onSignOut, onDeleteAccount, isPro, isAtFreeLimit, onUpgrade, onManageSubscription, onRestorePurchases, onFeedback }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const navItems = [
    { id: "dashboard", label: "🏠 Dashboard" },
    ...(accounts.length > 0 ? [{ id: "strategy", label: "📊 Strategies" }] : []),
    { id: "calculators", label: "🧮 Calculators" },
    { id: "amortization", label: "📅 Amortization" },
    { id: "calendar", label: "🗓 Calendar" },
    ...(isPro ? [{ id: "milestones", label: "🏆 Milestones" }] : [{ id: "milestones", label: "🔒 Milestones" }]),
  ];

  const handleNav = (id) => { setScreen(id); setMenuOpen(false); };

  return (
    <header style={navStyles.header}>
      <div style={{ ...navStyles.inner, ...(isMobile ? { padding: "0 16px", height: 52 } : {}) }}>
        <div style={{ ...navStyles.logoBox, cursor: "pointer" }} onClick={() => setScreen("dashboard")}>
          <span style={navStyles.logoText}>EL</span>
          <span style={navStyles.logoAccent}>DE</span>
        </div>

        {isMobile ? (
          // Mobile: hamburger + add button
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
            <button style={{ ...navStyles.addBtn, ...(isAtFreeLimit ? { background: "#94a3b8" } : {}) }}
              onClick={() => isAtFreeLimit ? onUpgrade() : openAddModal()}
            >{isAtFreeLimit ? "Upgrade to Pro" : "+ Add"}</button>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{ background: "none", border: "1px solid #334", borderRadius: 8, color: "#fff", padding: "6px 10px", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
            >
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        ) : (
          // Desktop: full nav
          <div style={navStyles.actions}>
            <button style={navStyles.hideBtn} onClick={() => setHideBalances(h => !h)}>
              {hideBalances ? "👁 Show" : "🙈 Hide"} Balances
            </button>
            {navItems.map(item => (
              <button key={item.id} style={{ ...navStyles.btn, ...(screen === item.id ? navStyles.btnActive : {}) }} onClick={() => setScreen(item.id)}>
                {item.label}
              </button>
            ))}
            <button style={{ ...navStyles.addBtn, ...(isAtFreeLimit ? { background: "#94a3b8", cursor: "not-allowed" } : {}) }}
              onClick={() => isAtFreeLimit ? onUpgrade() : openAddModal()}
              title={isAtFreeLimit ? "Upgrade to Pro to add more accounts" : "Add account"}
            >
              {isAtFreeLimit ? "🔒 Add Account" : "+ Add Account"}
            </button>
            <div style={{ position: "relative" }}>
              <button style={{ ...navStyles.btn, background: "none", border: "1px solid #334", borderRadius: 8, padding: "6px 12px" }} onClick={() => setMenuOpen(o => !o)}>⚙️</button>
              {menuOpen && (
                <>
                  <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={() => setMenuOpen(false)} />
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: "#1a1a2e", border: "1px solid #2a3a5e", borderRadius: 10, zIndex: 1000, minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", overflow: "hidden" }}>
                    {isPro && <button onClick={() => { onManageSubscription(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "none", border: "none", color: "#00BCD4", fontSize: 13, cursor: "pointer" }}>💳 Manage Subscription</button>}
                    <button onClick={() => { onRestorePurchases && onRestorePurchases(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "none", border: "none", color: "#8899BB", fontSize: 13, cursor: "pointer" }}>🔄 Restore Purchases</button>
                    <button onClick={() => { onSignOut(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "none", border: "none", color: "#aaa", fontSize: 13, cursor: "pointer" }}>🚪 Sign Out</button>
                    <button onClick={() => { onDeleteAccount(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "none", border: "none", color: "#EF4444", fontSize: 13, cursor: "pointer" }}>🗑 Delete Account</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={() => setMenuOpen(false)} />
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, background: "#1a1a2e",
            borderTop: "1px solid #2a3a5e", zIndex: 1000, padding: "8px 0", boxShadow: "0 8px 24px rgba(0,0,0,0.3)"
          }}>
            <button
              style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 20px", background: "none", border: "none", color: "#aaa", fontSize: 14, cursor: "pointer" }}
              onClick={() => { setHideBalances(h => !h); setMenuOpen(false); }}
            >
              {hideBalances ? "👁 Show Balances" : "🙈 Hide Balances"}
            </button>
            {navItems.map(item => (
              <button
                key={item.id}
                style={{
                  display: "block", width: "100%", textAlign: "left", padding: "12px 20px",
                  background: screen === item.id ? "#2a3a6e" : "none", border: "none",
                  color: screen === item.id ? "#fff" : "#aaa", fontSize: 14, cursor: "pointer",
                  borderLeft: screen === item.id ? "3px solid #4F7FFA" : "3px solid transparent"
                }}
                onClick={() => handleNav(item.id)}
              >
                {item.label}
              </button>
            ))}
            <div style={{ borderTop: "1px solid #2a3a5e", marginTop: 4 }}>
              {isPro && <button onClick={() => { onManageSubscription(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 20px", background: "none", border: "none", color: "#00BCD4", fontSize: 14, cursor: "pointer" }}>💳 Manage Subscription</button>}
              <button onClick={() => { onRestorePurchases && onRestorePurchases(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 20px", background: "none", border: "none", color: "#8899BB", fontSize: 14, cursor: "pointer" }}>🔄 Restore Purchases</button>
            </div>
            <div style={{ borderTop: "1px solid #2a3a5e", marginTop: 4 }}>
              <div style={{ padding: "8px 20px 4px", fontSize: 11, fontWeight: 700, color: "#556", textTransform: "uppercase", letterSpacing: 0.5 }}>Feedback</div>
              <button onClick={() => { onFeedback && onFeedback("Bug Report"); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 20px", background: "none", border: "none", color: "#aaa", fontSize: 14, cursor: "pointer" }}>🐛 Report a Bug</button>
              <button onClick={() => { onFeedback && onFeedback("Feature Suggestion"); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 20px", background: "none", border: "none", color: "#aaa", fontSize: 14, cursor: "pointer" }}>💡 Feature Suggestion</button>
              <button onClick={() => { onFeedback && onFeedback("General Feedback"); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 20px", background: "none", border: "none", color: "#aaa", fontSize: 14, cursor: "pointer" }}>👍 General Feedback</button>
            </div>
            <div style={{ borderTop: "1px solid #2a3a5e", marginTop: 4 }}>
              <button onClick={() => { onSignOut(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 20px", background: "none", border: "none", color: "#aaa", fontSize: 14, cursor: "pointer" }}>🚪 Sign Out</button>
              <button onClick={() => { onDeleteAccount(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 20px", background: "none", border: "none", color: "#EF4444", fontSize: 14, cursor: "pointer" }}>🗑 Delete Account</button>
            </div>
          </div>
        </>
      )}
    </header>
  );
}

function FeedbackButton({ isMobile }) {
  const [showMenu, setShowMenu] = useState(false);
  const FORM_URL = "https://docs.google.com/forms/d/1QL-RqpZLkm-PTV6AP7NlvVG6q06pFA_FYAXVB7xMLtY/viewform";

  const openForm = (category) => {
    const url = `${FORM_URL}?entry.category=${encodeURIComponent(category)}`;
    window.open(url, "_blank");
    setShowMenu(false);
  };

  return (
    <div style={{ position: "fixed", bottom: isMobile ? 100 : 120, right: isMobile ? 16 : 28, zIndex: 2500 }}>
      {showMenu && (
        <>
          <div style={{ position: "fixed", inset: 0 }} onClick={() => setShowMenu(false)} />
          <div style={{ ...fbStyles.menu, ...(isMobile ? { width: "calc(100vw - 32px)", right: 0 } : {}) }}>
            <div style={fbStyles.menuTitle}>How can we help?</div>
            <button style={fbStyles.menuBtn} onClick={() => openForm("Bug Report")}>
              <span style={{ fontSize: 18 }}>🐛</span>
              <div>
                <div style={fbStyles.menuBtnTitle}>Report a Bug</div>
                <div style={fbStyles.menuBtnDesc}>Something not working right?</div>
              </div>
            </button>
            <button style={fbStyles.menuBtn} onClick={() => openForm("Feature Suggestion")}>
              <span style={{ fontSize: 18 }}>💡</span>
              <div>
                <div style={fbStyles.menuBtnTitle}>Feature Suggestion</div>
                <div style={fbStyles.menuBtnDesc}>Have an idea to improve ELDE?</div>
              </div>
            </button>
            <button style={fbStyles.menuBtn} onClick={() => openForm("General Feedback")}>
              <span style={{ fontSize: 18 }}>👍</span>
              <div>
                <div style={fbStyles.menuBtnTitle}>General Feedback</div>
                <div style={fbStyles.menuBtnDesc}>Share your thoughts</div>
              </div>
            </button>
          </div>
        </>
      )}
      <button style={fbStyles.fab} onClick={() => setShowMenu(s => !s)}>
        <span style={{ fontSize: 18 }}>💬</span>
        <span style={{ fontSize: 11, fontWeight: 700 }}>Feedback</span>
      </button>
    </div>
  );
}

const fbStyles = {
  fab: { display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "#1a1a2e", color: "#fff", border: "1.5px solid #334", borderRadius: 50, padding: "12px 16px", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.3)", fontSize: 13, fontWeight: 700 },
  menu: { position: "absolute", bottom: 70, right: 0, background: "#fff", borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.15)", padding: "16px", width: 260, border: "1px solid #E5E7EB" },
  menuTitle: { fontSize: 13, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 },
  menuBtn: { display: "flex", alignItems: "center", gap: 12, width: "100%", background: "#F8F9FC", border: "none", borderRadius: 10, padding: "12px 14px", cursor: "pointer", marginBottom: 8, textAlign: "left" },
  menuBtnTitle: { fontSize: 14, fontWeight: 600, color: "#1a1a2e", marginBottom: 2 },
  menuBtnDesc: { fontSize: 12, color: "#888" },
};

const navStyles = {
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)", position: "sticky", top: "env(safe-area-inset-top, 0px)", zIndex: 1000 },
  inner: { maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, gap: 16 },
  logoBox: { display: "inline-flex", alignItems: "center", border: "2.5px solid #00BCD4", borderRadius: 4, padding: "2px 8px", background: "#000", width: "fit-content", flexShrink: 0 },
  logoText: { fontSize: 20, fontWeight: 900, color: "#fff", letterSpacing: 2 },
  logoAccent: { fontSize: 20, fontWeight: 900, color: "#00BCD4", letterSpacing: 2 },
  actions: { display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" },
  hideBtn: { background: "none", border: "1px solid #334", borderRadius: 8, color: "#aaa", padding: "6px 10px", fontSize: 12, cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" },
  btn: { background: "#1e2d4a", color: "#ccc", border: "1px solid #334", borderRadius: 8, padding: "6px 12px", fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
  btnActive: { background: "#4F7FFA", color: "#fff", border: "1px solid #4F7FFA" },
  addBtn: { background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
};

function TransactionHistory({ accounts, allPayments, hideBalances, onAccountClick, onAmountClick }) {
  const isMobile = useIsMobile();
  const [txnFilter, setTxnFilter] = useState("all");
  const [txnTypeFilter, setTxnTypeFilter] = useState("all");
  const [txnExpanded, setTxnExpanded] = useState(true);

  const allTxns = accounts.flatMap((acct, i) => {
    const acctPayments = allPayments[acct.id] || [];
    return acctPayments.map((p) => ({ ...p, accountName: acct.name, accountType: acct.type, colorIdx: i }));
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const filtered = allTxns.filter((t) => {
    const accountMatch = txnFilter === "all" || t.accountName === txnFilter;
    const typeMatch = txnTypeFilter === "all" || t.type === txnTypeFilter;
    return accountMatch && typeMatch;
  });

  const totalPaid = filtered.reduce((s, t) => s + t.amount, 0);
  const totalPrincipal = filtered.reduce((s, t) => s + (t.principal || 0), 0);
  const totalInterest = filtered.reduce((s, t) => s + (t.interest || 0), 0);

  return (
    <div style={txnStyles.section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: txnExpanded ? 20 : 0, cursor: "pointer" }} onClick={() => setTxnExpanded((e) => !e)}>
        <div>
          <h2 style={txnStyles.title}>🧾 Transaction History</h2>
          <div style={txnStyles.subtitle}>{allTxns.length} total payment{allTxns.length !== 1 ? "s" : ""} across all accounts</div>
        </div>
        <div style={{ fontSize: 20, color: "#888" }}>{txnExpanded ? "▲" : "▼"}</div>
      </div>

      {txnExpanded && (
        <>
          <div style={{ ...txnStyles.summaryGrid, ...(isMobile ? { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 8 } : {}) }}>
            {[
              { label: "Total Paid", value: formatCurrency(totalPaid), color: "#4F7FFA" },
              { label: "Total Principal", value: formatCurrency(totalPrincipal), color: "#10B981" },
              { label: "Total Interest Paid", value: formatCurrency(totalInterest), color: "#EF4444" },
              { label: "Transactions", value: filtered.length, color: "#A855F7" },
            ].map((card) => (
              <div key={card.label} style={txnStyles.summaryCard}>
                <div style={txnStyles.summaryLabel}>{card.label}</div>
                <div style={{ ...txnStyles.summaryValue, color: card.color }}>{hideBalances && card.label !== "Transactions" ? "••••••" : card.value}</div>
              </div>
            ))}
          </div>

          <div style={{ ...txnStyles.filters, ...(isMobile ? { flexDirection: "column", gap: 10 } : {}) }}>
            <div style={txnStyles.filterGroup}>
              <div style={txnStyles.filterLabel}>Account</div>
              <select style={txnStyles.select} value={txnFilter} onChange={(e) => setTxnFilter(e.target.value)}>
                <option value="all">All Accounts</option>
                {accounts.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
              </select>
            </div>
            <div style={txnStyles.filterGroup}>
              <div style={txnStyles.filterLabel}>Payment Type</div>
              <select style={txnStyles.select} value={txnTypeFilter} onChange={(e) => setTxnTypeFilter(e.target.value)}>
                <option value="all">All Types</option>
                {["Minimum Payment", "Extra Payment", "Full Payoff", "Partial Payment", "Other"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>🧾</div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No transactions yet</div>
              <div style={{ fontSize: 13 }}>Use "Log & Mark Paid" or log payments from Account Details to see them here.</div>
            </div>
          ) : isMobile ? (
            // Mobile card layout
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map((t, i) => {
                const color = COLORS[t.colorIdx % COLORS.length];
                return (
                  <div key={`${t.id}-${t.accountName}`} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff", borderRadius: 10, padding: "12px 14px", border: "1px solid #F0F0F5" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: color + "22", color, cursor: onAccountClick ? "pointer" : "default" }} onClick={() => onAccountClick && onAccountClick(accounts.find(a => a.name === t.accountName))}>{t.accountName}</span>
                        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{t.type}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, fontSize: 15, cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }} onClick={() => onAmountClick && onAmountClick(t, accounts.find(a => a.name === t.accountName))}>
                          {hideBalances ? "••••••" : formatCurrency(t.amount)}
                        </div>
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{new Date(t.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                      <span>Principal: <strong style={{ color: "#10B981" }}>{hideBalances ? "••" : formatCurrency(t.principal || 0)}</strong></span>
                      <span>Interest: <strong style={{ color: "#EF4444" }}>{hideBalances ? "••" : formatCurrency(t.interest || 0)}</strong></span>
                      {t.fees > 0 && <span>Fees: <strong style={{ color: "#F59E0B" }}>{hideBalances ? "••" : formatCurrency(t.fees)}</strong></span>}
                    </div>
                    {t.notes && <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{t.notes}</div>}
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop table layout
            <div style={{ overflowX: "auto" }}>
              <table style={txnStyles.table}>
                <thead>
                  <tr>
                    {["Date", "Account", "Type", "Amount", "Principal", "Interest", "Fees", "Notes"].map((h) => (
                      <th key={h} style={txnStyles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t, i) => {
                    const color = COLORS[t.colorIdx % COLORS.length];
                    return (
                      <tr key={`${t.id}-${t.accountName}`} style={i % 2 === 0 ? txnStyles.trEven : {}}>
                        <td style={txnStyles.td}>{new Date(t.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                        <td style={txnStyles.td}><span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: color + "22", color, cursor: onAccountClick ? "pointer" : "default" }} onClick={() => onAccountClick && onAccountClick(accounts.find(a => a.name === t.accountName))}>{t.accountName}</span></td>
                        <td style={txnStyles.td}><span style={{ fontSize: 11, fontWeight: 600, color: "#888" }}>{t.type}</span></td>
                        <td style={{ ...txnStyles.td, fontWeight: 700 }}>
                          <span style={{ cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }} title="Click to see interest calculation" onClick={() => onAmountClick && onAmountClick(t, accounts.find(a => a.name === t.accountName))}>
                            {hideBalances ? "••••••" : formatCurrency(t.amount)}
                          </span>
                        </td>
                        <td style={{ ...txnStyles.td, color: "#10B981" }}>{hideBalances ? "••" : formatCurrency(t.principal || 0)}</td>
                        <td style={{ ...txnStyles.td, color: "#EF4444" }}>{hideBalances ? "••" : formatCurrency(t.interest || 0)}</td>
                        <td style={{ ...txnStyles.td, color: "#F59E0B" }}>{t.fees > 0 ? (hideBalances ? "••" : formatCurrency(t.fees)) : "—"}</td>
                        <td style={{ ...txnStyles.td, color: "#888", fontSize: 12 }}>{t.notes || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const txnStyles = {
  section: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: "28px", marginTop: 24 },
  title: { fontSize: 18, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 4 },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 },
  summaryCard: { background: "#F8F9FC", borderRadius: 10, padding: "14px 16px" },
  summaryLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  summaryValue: { fontSize: 20, fontWeight: 800 },
  filters: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #F0F0F5" },
  filterGroup: { display: "flex", flexDirection: "column", gap: 6 },
  filterLabel: { fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  select: { border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "7px 12px", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: { textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", borderBottom: "2px solid #F0F0F5" },
  td: { padding: "10px 12px", borderBottom: "1px solid #F8F9FC" },
  trEven: { background: "#FAFAFA" },
};

function EditModal({ editingAccount, form, setForm, errors, setErrors, closeModal, handleSave, notificationDaysBefore = [], onNotificationChange }) {
  const isMobile = useIsMobile();
  let isNative = false;
  try { isNative = !!(window.Capacitor?.isNativePlatform?.()); } catch (e) {}
  // Ensure reminders is always an array (old data may be a single number)
  const reminders = Array.isArray(notificationDaysBefore) ? notificationDaysBefore : (notificationDaysBefore != null ? [notificationDaysBefore] : []);
  return (
    <div style={{ ...styles.modalOverlay, ...(isMobile ? { alignItems: "flex-end", padding: 0 } : {}) }} onClick={closeModal}>
      <div style={{ ...styles.modal, ...(isMobile ? { maxWidth: "100%", maxHeight: "92vh", borderRadius: "16px 16px 0 0", display: "flex", flexDirection: "column" } : {}) }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{editingAccount ? `Edit — ${editingAccount.name}` : "Add Debt Account"}</h3>
          <button style={styles.modalClose} onClick={closeModal}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Account Name</label>
            <input
              style={{ ...styles.input, ...(errors.name ? styles.inputError : {}) }}
              type="text"
              placeholder="e.g. Chase Visa"
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({ ...errors, name: null }); }}
            />
            {errors.name && <div style={styles.errorText}>{errors.name}</div>}
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Debt Type</label>
            <select style={styles.input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {DEBT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          {[
            { label: "Current Balance ($)", key: "balance", placeholder: "e.g. 5000", type: "number", min: "0" },
            { label: "Interest Rate (%)", key: "rate", placeholder: "e.g. 19.99", type: "number", min: "0" },
            { label: "Minimum Payment ($/mo)", key: "minPayment", placeholder: "e.g. 100", type: "number", min: "0" },
            { label: "Next Payment Due Date", key: "paymentDate", placeholder: "", type: "date", min: "", max: "" },
          ].map((field) => (
            <div key={field.key} style={styles.formGroup}>
              <label style={styles.label}>{field.label}</label>
              <input
                style={{ ...styles.input, ...(errors[field.key] ? styles.inputError : {}) }}
                type={field.type}
                min={field.min || "0"}
                max={field.max || undefined}
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={(e) => { setForm({ ...form, [field.key]: e.target.value }); setErrors({ ...errors, [field.key]: null }); }}
              />
              {errors[field.key] && <div style={styles.errorText}>{errors[field.key]}</div>}
              {field.key === "balance" && form.type === "Credit Card" && form.balance && (
                <div style={{ fontSize: 11, color: "#4F7FFA", background: "#EEF2FF", borderRadius: 6, padding: "6px 10px", marginTop: 4, lineHeight: 1.5 }}>
                  💳 Made new purchases? Update this balance to keep your payoff plan accurate.
                </div>
              )}
            </div>
          ))}
          <div style={styles.formGroup}>
            <label style={styles.label}>Last Payment Date (optional)</label>
            <input
              style={styles.input}
              type="date"
              value={form.lastPaymentDate}
              onChange={(e) => setForm({ ...form, lastPaymentDate: e.target.value })}
            />
            <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>Used to accurately calculate interest on your next payment</div>
          </div>
          {(() => { try { return isNative && form.paymentDate && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Payment Reminders</label>
              {reminders.map((days, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  {days !== 0 && <input
                    type="number"
                    min="0"
                    value={days % 7 === 0 && days >= 7 ? days / 7 : days}
                    onChange={(e) => {
                      const val = Math.max(0, parseInt(e.target.value) || 0);
                      const isWeeks = days % 7 === 0 && days >= 7;
                      const newDays = isWeeks ? val * 7 : val;
                      const updated = [...reminders];
                      updated[idx] = newDays;
                      onNotificationChange(updated);
                    }}
                    style={{ ...styles.input, width: 70, textAlign: "center" }}
                  />}
                  <select
                    style={{ ...styles.input, flex: 1 }}
                    value={days === 0 ? "dayof" : days % 7 === 0 && days >= 7 ? "weeks" : "days"}
                    onChange={(e) => {
                      if (e.target.value === "dayof") {
                        const updated = [...reminders];
                        updated[idx] = 0;
                        onNotificationChange(updated);
                      } else {
                        const isWeeks = e.target.value === "weeks";
                        const currentNum = days % 7 === 0 && days >= 7 ? days / 7 : days || 1;
                        const newDays = isWeeks ? currentNum * 7 : currentNum;
                        const updated = [...reminders];
                        updated[idx] = newDays;
                        onNotificationChange(updated);
                      }
                    }}
                  >
                    <option value="dayof">day of due date</option>
                    <option value="days">days before</option>
                    <option value="weeks">weeks before</option>
                  </select>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const updated = reminders.filter((_, i) => i !== idx);
                      onNotificationChange(updated.length ? updated : []);
                    }}
                    style={{ background: "none", border: "none", color: "#EF4444", fontSize: 18, cursor: "pointer", padding: "0 4px", flexShrink: 0 }}
                  >✕</button>
                </div>
              ))}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNotificationChange([...reminders, 3]);
                }}
                style={{ background: "#EEF2FF", border: "1.5px solid #C7D2FE", borderRadius: 8, color: "#4F7FFA", fontSize: 13, fontWeight: 600, padding: "8px 14px", cursor: "pointer", width: "100%", marginTop: 4 }}
              >+ Add Reminder</button>
              <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>Notifications fire at 9:00 AM. Only notifies if payment hasn't been marked paid.</div>
            </div>
          ); } catch(e) { return null; } })()}
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.cancelBtn} onClick={closeModal}>Cancel</button>
          <button style={styles.addBtn} onClick={handleSave}>{editingAccount ? "Save Changes" : "Add Account"}</button>
        </div>
      </div>
    </div>
  );
}

// First-run intro. Activation screen shown to brand-new users before they add
// their first debt — the biggest funnel drop is app_opened -> account_added.
const INTRO_SLIDES = [
  { icon: "🎯", title: "Know your real debt-free date", body: "Your bank only shows the minimum. ELDE shows the truth — exactly when you'll be debt-free and what the interest is really costing you." },
  { icon: "💸", title: "See what paying extra saves you", body: "Add a little to any payment and instantly watch the interest you avoid and the months you cut off your payoff." },
  { icon: "📊", title: "Follow one clear payoff plan", body: "Avalanche or snowball — ELDE orders your debts and tracks every payment so you always know your next move." },
];

function IntroScreen({ isMobile, onAddDebt, onSkip }) {
  const [slide, setSlide] = useState(0);
  const touchX = React.useRef(null);
  useEffect(() => { track("screen_viewed", { screen: "intro" }); }, []);
  const last = slide === INTRO_SLIDES.length - 1;
  const next = () => setSlide((s) => Math.min(s + 1, INTRO_SLIDES.length - 1));
  const prev = () => setSlide((s) => Math.max(s - 1, 0));
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -40) next(); else if (dx > 40) prev();
    touchX.current = null;
  };
  const s = INTRO_SLIDES[slide];
  const primaryBtn = { width: "100%", background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 12, padding: 16, fontSize: 16, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 20px rgba(79,127,250,0.45)" };
  const ghostBtn = { width: "100%", background: "none", border: "none", color: "#8899bb", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 14, marginTop: 4 };
  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ position: "fixed", inset: 0, zIndex: 4000, background: "linear-gradient(160deg, #0f0f1a 0%, #1a1a2e 60%, #1e2a4a 100%)", display: "flex", flexDirection: "column", color: "#fff", paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", border: "2px solid #00BCD4", borderRadius: 4, padding: "2px 8px", background: "#000" }}>
          <span style={{ fontSize: 16, fontWeight: 900, color: "#fff", letterSpacing: 2 }}>EL</span>
          <span style={{ fontSize: 16, fontWeight: 900, color: "#00BCD4", letterSpacing: 2 }}>DE</span>
        </div>
        {!last && <button onClick={onSkip} style={{ background: "none", border: "none", color: "#8899bb", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Skip</button>}
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 28px", maxWidth: 520, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ fontSize: 72, marginBottom: 28 }}>{s.icon}</div>
        <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, lineHeight: 1.2, margin: "0 0 16px" }}>{s.title}</h1>
        <p style={{ fontSize: isMobile ? 15 : 17, lineHeight: 1.6, color: "#aab4cc", margin: 0 }}>{s.body}</p>
      </div>
      <div style={{ padding: "0 28px 36px", maxWidth: 520, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 24 }}>
          {INTRO_SLIDES.map((_, i) => (
            <div key={i} onClick={() => setSlide(i)} style={{ width: i === slide ? 22 : 8, height: 8, borderRadius: 4, background: i === slide ? "#4F7FFA" : "#3a4560", cursor: "pointer", transition: "all 0.2s" }} />
          ))}
        </div>
        {last ? (
          <>
            <button onClick={onAddDebt} style={primaryBtn}>Add your first debt</button>
            <button onClick={onSkip} style={ghostBtn}>Maybe later</button>
          </>
        ) : (
          <button onClick={next} style={primaryBtn}>Next</button>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = React.useState(null);
  const [showAuth, setShowAuth] = React.useState(false);
  const [authMode, setAuthMode] = React.useState("login");
  const [authLoading, setAuthLoading] = React.useState(true);
  const [dbLoaded, setDbLoaded] = React.useState(false);
  const [isPro, setIsPro] = React.useState(() => {
    try { return localStorage.getItem("elde_is_pro") === "true"; } catch { return false; }
  });
  const FREE_ACCOUNT_LIMIT = 3;
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);

  // Notification settings — { [accountId]: daysBefore (0=day of, 1=day before, etc.) }
  const [notificationSettings, setNotificationSettings] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("elde_notifications") || "{}");
      // Normalize any old single-number values to arrays
      const normalized = {};
      Object.entries(saved).forEach(([k, v]) => {
        normalized[k] = Array.isArray(v) ? v : (v != null ? [v] : []);
      });
      return normalized;
    } catch { return {}; }
  });

  // RevenueCat — native purchase management
  const { rcIsPro, purchasePlan, restorePurchases } = useRevenueCat(session?.user?.id);
  // Merge RC pro status with Supabase isPro (Supabase acts as fallback for web)
  const effectiveIsPro = isPro || rcIsPro;

  // Handle ?pro=success redirect from Stripe
  // Analytics: init once, track app open, and identify the user (Supabase id only).
  React.useEffect(() => {
    initAnalytics();
    track("app_opened");
  }, []);
  React.useEffect(() => {
    if (session?.user?.id) identify(session.user.id);
  }, [session]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pro") === "success") {
      setIsPro(true);
      try { localStorage.setItem("elde_is_pro", "true"); } catch {}
      window.history.replaceState({}, "", window.location.pathname);
      setShowUpgradeModal(false);
    }
  }, []);

  const handleManageSubscription = async () => {
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    if (isNative) {
      alert(
        "To manage your subscription, go to:\n\n" +
        "Google Play → Account → Payments & subscriptions → Subscriptions\n\n" +
        "Or use 'Restore Purchases' in the menu if you've already subscribed on another device."
      );
    } else {
      if (!session) return;
      try {
        const res = await fetch("/api/customer-portal", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: session.user.id, email: session.user.email }),
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          alert("Could not open subscription portal. Please try again.");
        }
      } catch (err) {
        console.error("Portal error:", err);
        alert("Could not open subscription portal. Please try again.");
      }
    }
  };

  const handleUpgrade = async (plan = "monthly") => {
    if (!session) return;
    const isNative = !!(window.Capacitor?.isNativePlatform?.());

    if (isNative) {
      // Native: use RevenueCat
      const platform = window.Capacitor?.getPlatform?.() || "native"; // "ios" | "android"
      setCheckoutLoading(true);
      const result = await purchasePlan(plan);
      setCheckoutLoading(false);
      if (result.success) {
        setIsPro(true);
        track("purchase_completed", { plan, platform });
        try { localStorage.setItem("elde_is_pro", "true"); } catch {}
        setShowUpgradeModal(false);
        alert("🎉 Welcome to ELDE Pro!");
      } else if (result.error === "cancelled") {
        // User cancelled — do nothing
      } else if (result.error !== "not_native") {
        alert("Purchase failed: " + (result.error || "unknown error") + ". Please try again or contact elde.support@gmail.com");
      }
    } else {
      // Web: Stripe fallback
      setCheckoutLoading(true);
      try {
        const res = await fetch("/api/create-checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: session.user.id, email: session.user.email, plan }),
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          alert("Could not start checkout. Please try again.");
        }
      } catch (err) {
        console.error("Checkout error:", err);
        alert("Could not start checkout. Please try again.");
      } finally {
        setCheckoutLoading(false);
      }
    }
  };
  const [showDeleteAccountModal, setShowDeleteAccountModal] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState("");

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    // Clear localStorage on sign out
    try {
      localStorage.removeItem("elde_accounts");
      localStorage.removeItem("elde_payments");
      localStorage.removeItem("elde_plan");
      localStorage.removeItem("elde_is_pro");
    } catch {}
    setIsPro(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    try {
      const userId = session.user.id;
      // Delete all user data from Supabase
      await supabase.from("accounts").delete().eq("user_id", userId);
      await supabase.from("payments").delete().eq("user_id", userId);
      await supabase.from("settings").delete().eq("user_id", userId);
      // Delete the auth user
      await supabase.rpc("delete_user");
      // Sign out
      await supabase.auth.signOut();
      try { localStorage.clear(); } catch {}
    } catch (err) {
      console.error("Error deleting account:", err);
      alert("Error deleting account. Please contact elde.support@gmail.com");
    }
    setShowDeleteAccountModal(false);
  };

  React.useEffect(() => {
    // Safety timeout — if auth check takes too long, proceed anyway
    const timeout = setTimeout(() => setAuthLoading(false), 3000);
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      setSession(session);
      setAuthLoading(false);
    });
    // Listen for auth changes for the lifetime of the app
    supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setAuthLoading(false);
      if (event === "SIGNED_IN" && session) {
        setSession(session);
        setShowAuth(false);
      }
    });

    // Handle deep link redirects from OAuth on native (Capacitor)
    const handleDeepLinkUrl = async (url) => {
      if (!url || !url.startsWith("app.elde.mobile://")) return;
      // Close the in-app browser (SFSafariViewController) when deep link fires
      try { await Browser.close(); } catch {}
      const fragment = url.includes("#") ? url.split("#")[1] : url.split("?")[1] || "";
      const params = new URLSearchParams(fragment);
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");

      if (accessToken && refreshToken) {
        const { data } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (data?.session) {
          setSession(data.session);
          setShowAuth(false);
          setAuthLoading(false);
          return;
        }
      }

      // Fallback: poll getSession in case tokens are still being stored
      for (let i = 0; i < 5; i++) {
        await new Promise(res => setTimeout(res, 500));
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          setSession(sessionData.session);
          setShowAuth(false);
          setAuthLoading(false);
          return;
        }
      }
    };

    let deepLinkListener;
    try {
      // Check launch URL — catches deep link when app was closed and opened via redirect
      CapacitorApp.getLaunchUrl().then(({ url } = {}) => {
        if (url) handleDeepLinkUrl(url);
      }).catch(() => {});

      // Listen for deep links when app is already running
      CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        handleDeepLinkUrl(url);
      }).then(listener => { deepLinkListener = listener; });
    } catch (e) {
      // Not running in Capacitor, ignore
    }

    return () => {
      if (deepLinkListener) deepLinkListener.remove();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data from Supabase when user logs in
  React.useEffect(() => {
    if (!session) { setDbLoaded(false); return; }
    const userId = session.user.id;
    async function loadFromSupabase() {
      // Safety timeout — always show app after 5 seconds even if Supabase is slow
      const timeout = setTimeout(() => setDbLoaded(true), 5000);
      try {
        const [dbAccounts, dbPayments, dbSettings] = await Promise.all([
          fetchAccounts(userId),
          fetchPayments(userId),
          fetchSettings(userId),
        ]);
        if (dbAccounts && dbAccounts.length > 0) {
          setAccounts(dbAccounts);
          try { localStorage.setItem("elde_accounts", JSON.stringify(dbAccounts)); } catch {}
        } else {
          // New user — clear any stale localStorage data and reset state
          setAccounts([]);
          setAllPayments({});
          setSeenMilestones([]);
          try {
            localStorage.removeItem("elde_accounts");
            localStorage.removeItem("elde_payments");
            localStorage.removeItem("elde_seen_milestones");
            localStorage.removeItem("elde_monthly_history");
            localStorage.removeItem("elde_is_pro");
          } catch {}
          setIsPro(false);
        }
        if (dbPayments && Object.keys(dbPayments).length > 0) {
          setAllPayments(dbPayments);
          try { localStorage.setItem("elde_payments", JSON.stringify(dbPayments)); } catch {}
        }
        if (dbSettings) {
          setPlanSettings({ strategy: dbSettings.strategy, extraBudget: dbSettings.extraBudget });
          if (dbSettings.customOrder?.length) setCustomOrder(dbSettings.customOrder);
          if (dbSettings.easyMode !== undefined) setEasyMode(dbSettings.easyMode);
          if (dbSettings.monthOverrides) setMonthOverrides(dbSettings.monthOverrides);
          if (dbSettings.monthlyPaymentsHistory?.length) setMonthlyPaymentsHistory(dbSettings.monthlyPaymentsHistory);
          if (dbSettings.seenMilestones?.length) setSeenMilestones(dbSettings.seenMilestones);
          if (dbSettings.isPro) {
            setIsPro(true);
            try { localStorage.setItem("elde_is_pro", "true"); } catch {}
          }
        }
      } catch (err) {
        console.error("Error loading from Supabase:", err);
      } finally {
        clearTimeout(timeout);
        setDbLoaded(true);
      }
    }
    loadFromSupabase();
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Request notification permissions when user logs in
  React.useEffect(() => {
    if (session) requestNotificationPermission().catch(() => {});
  }, [session]);

  // Auth state checked after all hooks — see bottom of render

  const isMobile = useIsMobile();
  const [accounts, setAccounts] = useState(loadAccounts);
  const isAtFreeLimit = !effectiveIsPro && accounts.length >= FREE_ACCOUNT_LIMIT;
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [pendingReminders, setPendingReminders] = useState([]);
  const [hideBalances, setHideBalances] = useState(false);
  const [easyMode, setEasyMode] = useState(() => { try { return localStorage.getItem("elde_easy_mode") === "true"; } catch { return false; } });

  const [screen, setScreen] = useState("dashboard");
  // First-run intro: show once (gated by a flag so returning users never see it).
  const [introSeen, setIntroSeen] = useState(() => {
    try { return localStorage.getItem("elde_intro_seen") === "1"; } catch { return true; }
  });
  const dismissIntro = () => { setIntroSeen(true); try { localStorage.setItem("elde_intro_seen", "1"); } catch {} };
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [savedBanner, setSavedBanner] = useState(false);
  const [planSettings, setPlanSettings] = useState(loadPlanSettings);

  // Month overrides from variable schedule — lifted here so payment plan reacts to changes
  const [monthOverrides, setMonthOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("elde_month_overrides") || "{}"); } catch { return {}; }
  });

  // Re-sync when navigating back to dashboard in case PayoffStrategy changed overrides
  useEffect(() => {
    if (screen === "dashboard") {
      try {
        const fresh = JSON.parse(localStorage.getItem("elde_month_overrides") || "{}");
        setMonthOverrides(fresh);
      } catch {}
    }
  }, [screen]);



  // Effective extra for current month — uses month 1 override if set, else base
  const effectiveExtraBudget = monthOverrides["1"] !== undefined
    ? monthOverrides["1"]
    : planSettings.extraBudget;
  const [customOrder, setCustomOrder] = useState(loadCustomOrder);
  const [showAdvisor, setShowAdvisor] = useState(false);

  // Analytics: track navigation and key surface views (no money values).
  React.useEffect(() => { track("screen_viewed", { screen }); }, [screen]);
  React.useEffect(() => { if (showUpgradeModal) track("paywall_viewed"); }, [showUpgradeModal]);
  React.useEffect(() => { if (showAdvisor) track("advisor_opened"); }, [showAdvisor]);
  const [txnPopup, setTxnPopup] = useState(null);
  const [monthlyPayments, setMonthlyPayments] = useState(loadMonthlyPayments);

  // Reschedule notifications when accounts or payment status changes
  React.useEffect(() => {
    if (!session || !dbLoaded) return;
    rescheduleAllNotifications(accounts, notificationSettings, monthlyPayments).catch(() => {});
  }, [accounts, notificationSettings, monthlyPayments, dbLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const [quickPay, setQuickPay] = useState(null); // { account, colorIdx }
  const [quickPayForm, setQuickPayForm] = useState(null);
  const [allPayments, setAllPayments] = useState(loadPayments);
  const [monthlyPaymentsHistory, setMonthlyPaymentsHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("elde_monthly_history") || "[]"); } catch { return []; }
  });
  const [newMilestones, setNewMilestones] = useState([]);
  const [seenMilestones, setSeenMilestones] = useState(() => {
    try { return JSON.parse(localStorage.getItem("elde_seen_milestones") || "[]"); } catch { return []; }
  });
  const [showDailyInterest, setShowDailyInterest] = useState(false);
  const [paidCardMode, setPaidCardMode] = useState(0); // 0=Total Paid, 1=Principal Paid, 2=Interest Paid
  const [showMonthsReduced, setShowMonthsReduced] = useState(false);
  const [bulkPaidToast, setBulkPaidToast] = useState(null);
  const [extraPayModal, setExtraPayModal] = useState(null);
  const [extraPayAmount, setExtraPayAmount] = useState("");
  const [whatIfExtra, setWhatIfExtra] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedForPay, setSelectedForPay] = useState([]);

  const isFirstAccountsLoad = React.useRef(true);
  useEffect(() => {
    if (isFirstAccountsLoad.current) {
      isFirstAccountsLoad.current = false;
      return;
    }
    try {
      localStorage.setItem("elde_accounts", JSON.stringify(accounts));
      setSavedBanner(true);
      const t = setTimeout(() => setSavedBanner(false), 2000);
      return () => clearTimeout(t);
    } catch (e) {}
  }, [accounts]);

  useEffect(() => {
    try { localStorage.setItem("elde_plan", JSON.stringify(planSettings)); } catch (e) {}
    if (session) saveSettings(session.user.id, {
      strategy: planSettings.strategy,
      extraBudget: planSettings.extraBudget,
      customOrder,
      easyMode,
      monthOverrides,
      monthlyPaymentsHistory,
      seenMilestones,
    });
  }, [planSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { localStorage.setItem("elde_monthly_payments", JSON.stringify(monthlyPayments)); } catch (e) {}
  }, [monthlyPayments]);

  // Track paid months history for streak calculation
  useEffect(() => {
    const key = getCurrentMonthKey();
    const allDue = accounts.filter(a => a.paymentDate);
    const allPaid = allDue.length > 0 && allDue.every(a => !!monthlyPayments.paid[a.id]);
    if (allPaid && !monthlyPaymentsHistory.includes(key)) {
      const updated = [...monthlyPaymentsHistory, key];
      setMonthlyPaymentsHistory(updated);
      try { localStorage.setItem("elde_monthly_history", JSON.stringify(updated)); } catch {}
    }
  }, [monthlyPayments, accounts, monthlyPaymentsHistory]);

  // Detect new milestones — only after DB loaded and user has actual data
  useEffect(() => {
    if (!dbLoaded) return;
    if (accounts.length === 0 && Object.keys(allPayments).length === 0) return;
    const { earned } = calculateRewards(accounts, allPayments, monthlyPaymentsHistory);
    if (earned.length === 0) return;
    const fresh = earned.filter(id => !seenMilestones.includes(id));
    if (fresh.length > 0) {
      setNewMilestones(fresh);
      const updated = [...seenMilestones, ...fresh];
      setSeenMilestones(updated);
      try { localStorage.setItem("elde_seen_milestones", JSON.stringify(updated)); } catch {}
      if (session) saveSettings(session.user.id, {
        strategy: planSettings.strategy,
        extraBudget: planSettings.extraBudget,
        customOrder, easyMode, monthOverrides,
        monthlyPaymentsHistory, seenMilestones: updated,
      });
    }
  }, [accounts, allPayments, monthlyPaymentsHistory, dbLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateBalance = (accountId, newBalance) => {
    setAccounts((prev) => {
      const updated = prev.map((a) => a.id === accountId ? { ...a, balance: newBalance } : a);
      const acct = updated.find((a) => a.id === accountId);
      if (session && acct) saveAccount(session.user.id, acct);
      return updated;
    });
    setSelectedAccount((prev) => prev && prev.id === accountId ? { ...prev, balance: newBalance } : prev);
  };

  const handleUpdateLastPaymentDate = (accountId, date) => {
    setAccounts((prev) => {
      const updated = prev.map((a) => a.id === accountId
        ? { ...a, previousLastPaymentDate: a.lastPaymentDate, lastPaymentDate: date }
        : a);
      const acct = updated.find((a) => a.id === accountId);
      if (session && acct) saveAccount(session.user.id, acct);
      return updated;
    });
    setSelectedAccount((prev) => prev && prev.id === accountId
      ? { ...prev, previousLastPaymentDate: prev.lastPaymentDate, lastPaymentDate: date }
      : prev);
  };

  const toggleMonthlyPaid = (accountId) => {
    const key = getCurrentMonthKey();
    const [yr, mo] = key.split("-").map(Number);

    setMonthlyPayments((prev) => {
      const paid = { ...prev.paid };
      if (paid[accountId]) {
        // Undo: delete the payment logged this month and restore balance
        setAllPayments((prevPayments) => {
          const existing = prevPayments[accountId] || [];
          const thisMonthPayments = existing.filter(p => {
            const d = new Date(p.date + "T00:00:00");
            return d.getFullYear() === yr && d.getMonth() === mo - 1;
          });
          // Restore balance for each payment being removed
          const totalPrincipal = thisMonthPayments.reduce((s, p) => s + (p.principal || 0), 0);
          const acct = accounts.find(a => a.id === accountId);
          if (acct && totalPrincipal > 0) {
            handleUpdateBalance(accountId, acct.balance + totalPrincipal);
          }
          const updatedPayments = { ...prevPayments, [accountId]: existing.filter(p => {
            const d = new Date(p.date + "T00:00:00");
            return !(d.getFullYear() === yr && d.getMonth() === mo - 1);
          })};
          savePayments(updatedPayments);
          // Revert lastPaymentDate to previous value before payment was logged
          const remaining = updatedPayments[accountId] || [];
          const revertTo = remaining.length > 0
            ? remaining.reduce((latest, p) => p.date > latest.date ? p : latest).date
            : acct?.previousLastPaymentDate || acct?.originalLastPaymentDate || null;
          handleUpdateLastPaymentDate(accountId, revertTo);
          return updatedPayments;
        });
        delete paid[accountId];
      } else {
        paid[accountId] = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }
      return { monthKey: key, paid };
    });
  };

  const openQuickPay = (acct, colorIdx) => {
    const today = new Date().toISOString().split("T")[0];
    const days = getDaysBetween(acct.lastPaymentDate, today);
    const payAmount = acct.totalPayment || acct.minPayment;
    const { interest, principal } = calcSplit(payAmount, acct.balance, acct.rate, days);
    setQuickPay({ account: acct, colorIdx, plannedAmount: payAmount });
    setQuickPayForm({
      amount: String(payAmount),
      principal: String(principal),
      interest: String(interest),
      fees: "0",
      date: today,
      type: "Minimum Payment",
      notes: "",
    });
  };

  const handleQuickPayAmountChange = (val) => {
    if (!quickPay) return;
    const { account } = quickPay;
    const days = getDaysBetween(account.lastPaymentDate, quickPayForm.date);
    const { interest, principal } = calcSplit(parseFloat(val) || 0, account.balance, account.rate, days);
    setQuickPayForm((f) => ({ ...f, amount: val, interest: interest.toFixed(2), principal: principal.toFixed(2) }));
  };

  const handleQuickPayDateChange = (val) => {
    if (!quickPay) return;
    const { account } = quickPay;
    const days = getDaysBetween(account.lastPaymentDate, val);
    const { interest, principal } = calcSplit(parseFloat(quickPayForm.amount) || 0, account.balance, account.rate, days);
    setQuickPayForm((f) => ({ ...f, date: val, interest: interest.toFixed(2), principal: principal.toFixed(2) }));
  };

  const handleQuickPaySubmit = () => {
    if (!quickPay || !quickPayForm) return;
    const { account } = quickPay;
    const payment = {
      amount: parseFloat(quickPayForm.amount) || 0,
      principal: parseFloat(quickPayForm.principal) || 0,
      interest: parseFloat(quickPayForm.interest) || 0,
      fees: parseFloat(quickPayForm.fees) || 0,
      date: quickPayForm.date,
      type: quickPayForm.type,
      notes: quickPayForm.notes,
    };

    // Log the payment
    const updated = addPayment(allPayments, account.id, payment);
    setAllPayments(updated);
    savePayments(updated);
    if (session) savePayment(session.user.id, account.id, { ...payment, id: Date.now() });

    // Update balance
    if (payment.principal > 0) {
      handleUpdateBalance(account.id, Math.max(account.balance - payment.principal, 0));
    }

    // Update last payment date
    // Store previous lastPaymentDate before updating so undo can revert
    setAccounts(prev => prev.map(a => a.id === account.id
      ? { ...a, previousLastPaymentDate: a.lastPaymentDate }
      : a
    ));
    handleUpdateLastPaymentDate(account.id, payment.date);

    // Mark as paid in monthly tracker
    const key = getCurrentMonthKey();
    setMonthlyPayments((prev) => ({
      monthKey: key,
      paid: {
        ...prev.paid,
        [account.id]: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      },
    }));

    setQuickPay(null);
    setQuickPayForm(null);
  };

  const handleDeletePayment = (accountId, paymentId) => {
    if (session) deletePayment(session.user.id, paymentId);
    setAllPayments((prev) => {
      const existing = prev[accountId] || [];
      const updated = { ...prev, [accountId]: existing.filter(p => p.id !== paymentId) };
      savePayments(updated);
      return updated;
    });
  };

  const handleAddPayment = (accountId, payment) => {
    // Generate the ID once so localStorage and Supabase store the same record
    const paymentWithId = { ...payment, id: Date.now() };
    setAllPayments((prev) => {
      const existing = prev[accountId] || [];
      const updated = { ...prev, [accountId]: [paymentWithId, ...existing] };
      savePayments(updated);
      return updated;
    });
    if (session) savePayment(session.user.id, accountId, paymentWithId);
  };

  const handleMarkMonthPaid = (accountId, undo = false) => {
    const key = getCurrentMonthKey();
    setMonthlyPayments((prev) => {
      const paid = { ...prev.paid };
      if (undo) {
        delete paid[accountId];
      } else {
        paid[accountId] = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }
      return { monthKey: key, paid };
    });
  };

  const handleMarkAllPaid = () => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    const dateLabel = today.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const key = getCurrentMonthKey();
    const dueAccounts = paymentPlan.filter(isDueThisMonth);
    const unpaid = dueAccounts.filter(acct => selectedForPay.includes(acct.id) && !monthlyPayments.paid[acct.id]);
    if (unpaid.length === 0) return;

    unpaid.forEach(acct => {
      const days = getDaysBetween(acct.lastPaymentDate, todayStr);
      const payAmount = acct.totalPayment || acct.minPayment;
      const { interest, principal } = calcSplit(payAmount, acct.balance, acct.rate, days);
      const payment = {
        id: Date.now() + acct.id,
        date: todayStr,
        amount: payAmount,
        principal,
        interest,
        fees: 0,
        type: "Minimum Payment",
        notes: "Bulk marked paid",
      };
      setAllPayments(prev => {
        const updated = { ...prev, [acct.id]: [payment, ...(prev[acct.id] || [])] };
        savePayments(updated);
        if (session) savePayment(session.user.id, acct.id, payment);
        return updated;
      });
      handleUpdateBalance(acct.id, Math.max(acct.balance - principal, 0));
      handleUpdateLastPaymentDate(acct.id, todayStr);
    });

    setMonthlyPayments(prev => {
      const paid = { ...prev.paid };
      unpaid.forEach(acct => { paid[acct.id] = dateLabel; });
      return { monthKey: key, paid };
    });

    setBulkPaidToast(unpaid.length);
    setTimeout(() => setBulkPaidToast(null), 3000);
    setSelectionMode(false);
    setSelectedForPay([]);
  };

  const openExtraPay = (acct) => {
    setExtraPayAmount("");
    setExtraPayModal({ acct });
  };

  const handleExtraPay = () => {
    if (!extraPayModal) return;
    const { acct } = extraPayModal;
    const amount = parseFloat(extraPayAmount);
    if (!amount || amount <= 0) return;
    const today = new Date().toISOString().split("T")[0];
    const days = getDaysBetween(acct.lastPaymentDate, today);
    const { interest, principal } = calcSplit(amount, acct.balance, acct.rate, days);
    const payment = {
      id: Date.now(),
      date: today,
      amount,
      principal,
      interest,
      fees: 0,
      type: "Extra Payment",
      notes: "One-time extra payment",
    };
    setAllPayments(prev => {
      const updated = { ...prev, [acct.id]: [payment, ...(prev[acct.id] || [])] };
      savePayments(updated);
      if (session) savePayment(session.user.id, acct.id, payment);
      return updated;
    });
    handleUpdateBalance(acct.id, Math.max(acct.balance - principal, 0));
    handleUpdateLastPaymentDate(acct.id, today);
    track("extra_payment_entered", { account_type: acct.type });
    setExtraPayModal(null);
    setExtraPayAmount("");
  };

  const totalDebt = accounts.reduce((s, a) => s + a.balance, 0);
  const totalMin = accounts.reduce((s, a) => s + a.minPayment, 0);
  const totalMonthlyInterest = accounts.reduce((s, a) => s + (a.balance * (a.rate / 100)) / 12, 0);
  const allPaymentsList = Object.values(allPayments).flat();
  const grandTotalPaid = allPaymentsList.reduce((s, p) => s + (p.amount || 0), 0);
  const grandTotalPrincipal = allPaymentsList.reduce((s, p) => s + (p.principal || 0), 0);
  // Interest saved: compare actual interest paid vs projected interest at minimums only
  // for the same number of payments logged per account
  const interestSavedActual = accounts.reduce((total, acct) => {
    const payments = allPayments[acct.id] || [];
    if (payments.length === 0) return total;
    const totalPrincipalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
    const originalBalance = acct.balance + totalPrincipalPaid;
    const monthlyRate = (parseFloat(acct.rate) || 0) / 100 / 12;
    const minPayment = parseFloat(acct.minPayment) || 0;
    // Simulate minimum-only payments for the same number of months
    let simBalance = originalBalance;
    let projectedInterest = 0;
    for (let i = 0; i < payments.length && simBalance > 0.005; i++) {
      const interest = simBalance * monthlyRate;
      projectedInterest += interest;
      const principal = Math.min(minPayment - interest, simBalance);
      simBalance = Math.max(simBalance - (principal > 0 ? principal : 0), 0);
    }
    const actualInterest = payments.reduce((s, p) => s + Math.max((p.amount || 0) - (p.principal || 0), 0), 0);
    return total + Math.max(projectedInterest - actualInterest, 0);
  }, 0);
  const totalDailyInterest = accounts.reduce((s, a) => s + (a.balance * (a.rate / 100)) / 365, 0);
  const debtFreeDate = (() => {
    if (!accounts.length) return null;
    let maxMonths = 0;
    accounts.forEach(a => {
      const r = a.rate / 100 / 12;
      if (a.minPayment <= a.balance * r || a.balance <= 0) return;
      const months = Math.ceil(Math.log(a.minPayment / (a.minPayment - a.balance * r)) / Math.log(1 + r));
      if (months > maxMonths) maxMonths = months;
    });
    if (!maxMonths) return null;
    const d = new Date();
    d.setMonth(d.getMonth() + maxMonths);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  })();
  const paymentPlan = buildPaymentPlan(accounts, planSettings.strategy, effectiveExtraBudget, customOrder);

  // Months reduced: how many months sooner the debt-free date is vs paying only minimums
  // from the original balance. Driven by the longest-running account (same as debtFreeDate).
  const monthsReducedActual = (() => {
    if (!accounts.length) return 0;
    const payoffMonths = (balance, monthlyRate, minPayment) => {
      if (balance <= 0) return 0;
      if (minPayment <= balance * monthlyRate) return Infinity; // never pays off
      return Math.ceil(Math.log(minPayment / (minPayment - balance * monthlyRate)) / Math.log(1 + monthlyRate));
    };
    let maxReduced = 0;
    accounts.forEach((acct) => {
      const payments = allPayments[acct.id] || [];
      if (payments.length === 0) return;
      const totalPrincipalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
      const originalBalance = acct.balance + totalPrincipalPaid;
      const r = (parseFloat(acct.rate) || 0) / 100 / 12;
      const minPayment = parseFloat(acct.minPayment) || 0;
      // Baseline: months to pay off the ORIGINAL balance at minimums only
      const baselineMonths = payoffMonths(originalBalance, r, minPayment);
      // Actual path so far: months already paid + months remaining at minimums from CURRENT balance
      const remainingMonths = payoffMonths(acct.balance, r, minPayment);
      const actualMonths = payments.length + remainingMonths;
      if (Number.isFinite(baselineMonths) && Number.isFinite(actualMonths)) {
        maxReduced = Math.max(maxReduced, Math.max(baselineMonths - actualMonths, 0));
      }
    });
    return maxReduced;
  })();

  const isDueThisMonth = (acct) => {
    if (!acct.paymentDate) return true;
    const today = new Date();
    const nextDue = getNextDue(acct.paymentDate, acct.lastPaymentDate);
    if (!nextDue) return true;
    // Show if due date is in current month and hasn't been paid yet
    return nextDue.getFullYear() === today.getFullYear() && nextDue.getMonth() === today.getMonth();
  };

  const openAddModal = () => { setEditingAccount(null); setForm(emptyForm); setErrors({}); setShowModal(true); };

  const openEditModal = (acct) => {
    setEditingAccount(acct);
    setForm({
      name: acct.name,
      type: acct.type,
      balance: String(acct.balance),
      rate: String(acct.rate),
      minPayment: String(acct.minPayment),
      paymentDate: acct.paymentDate || "",
      lastPaymentDate: acct.lastPaymentDate || "",
    });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Account name is required";
    if (!form.balance || isNaN(form.balance) || Number(form.balance) <= 0) e.balance = "Enter a valid balance";
    if (!form.rate || isNaN(form.rate) || Number(form.rate) <= 0) e.rate = "Enter a valid interest rate";
    if (!form.minPayment || isNaN(form.minPayment) || Number(form.minPayment) <= 0) e.minPayment = "Enter a valid minimum payment";
    if (form.paymentDate && (!form.paymentDate.includes("-") || isNaN(new Date(form.paymentDate + "T00:00:00").getTime()))) {
      e.paymentDate = "Enter a valid date";
    }
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const updated = {
      name: form.name,
      type: form.type,
      balance: parseFloat(form.balance),
      rate: parseFloat(form.rate),
      minPayment: parseFloat(form.minPayment),
      paymentDate: form.paymentDate ? (form.paymentDate.includes("-") ? form.paymentDate : null) : null,
      lastPaymentDate: form.lastPaymentDate || null,
      originalLastPaymentDate: form.lastPaymentDate || null,
    };
    if (editingAccount) {
      const updatedAccounts = accounts.map((a) => a.id === editingAccount.id ? { ...a, ...updated } : a);
      setAccounts(updatedAccounts);
      if (session) saveAccount(session.user.id, { ...editingAccount, ...updated });
      // Keep selectedAccount in sync if we edited from details screen
      if (selectedAccount && selectedAccount.id === editingAccount.id) {
        setSelectedAccount({ ...editingAccount, ...updated });
      }
    } else {
      const newAcct = { id: Date.now(), ...updated };
      setAccounts([...accounts, newAcct]);
      if (session) saveAccount(session.user.id, newAcct);
      track("account_added", { account_count: accounts.length + 1, account_type: updated.type });
      if (pendingReminders.length > 0) {
        const updatedSettings = { ...notificationSettings, [newAcct.id]: pendingReminders };
        setNotificationSettings(updatedSettings);
        try { localStorage.setItem("elde_notifications", JSON.stringify(updatedSettings)); } catch {}
      }
    }
    setForm(emptyForm);
    setErrors({});
    setPendingReminders([]);
    setShowModal(false);
    setEditingAccount(null);
  };

  const handleDelete = (id) => {
    if (window.confirm("Remove this account?")) {
      setAccounts(accounts.filter((a) => a.id !== id));
      if (session) deleteAccount(session.user.id, id);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    setErrors({});
    setForm(emptyForm);
    setPendingReminders([]);
  };

  const handleRestorePurchases = async () => {
    const result = await restorePurchases();
    if (result.isPro) {
      setIsPro(true);
      try { localStorage.setItem("elde_is_pro", "true"); } catch {}
      alert("✅ Pro subscription restored!");
    } else {
      alert("No active subscription found.");
    }
  };

  const handleFeedback = (category) => {
    const FORM_URL = "https://docs.google.com/forms/d/1QL-RqpZLkm-PTV6AP7NlvVG6q06pFA_FYAXVB7xMLtY/viewform";
    const url = `${FORM_URL}?entry.category=${encodeURIComponent(category)}`;
    window.open(url, "_blank");
  };

  // Shared layout elements
  const sharedNav = <><div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "env(safe-area-inset-top, 0px)", background: "#1a1a2e", zIndex: 9999 }} /><NavHeader screen={screen} setScreen={setScreen} hideBalances={hideBalances} setHideBalances={setHideBalances} accounts={accounts} openAddModal={openAddModal} onSignOut={handleSignOut} onDeleteAccount={() => setShowDeleteAccountModal(true)} isPro={effectiveIsPro} isAtFreeLimit={isAtFreeLimit} onUpgrade={() => setShowUpgradeModal(true)} onManageSubscription={handleManageSubscription} onRestorePurchases={handleRestorePurchases} onFeedback={handleFeedback} />{!isMobile && <FeedbackButton isMobile={false} />}</>;
  const sharedEllie = !showAdvisor && (effectiveIsPro
    ? <button style={{ ...styles.ellieFloat, ...(isMobile ? { bottom: 16, right: 16, padding: "10px 14px" } : {}) }} onClick={() => setShowAdvisor(true)}><span style={{ fontSize: isMobile ? 20 : 24 }}>💬</span><span style={{ fontSize: 11, fontWeight: 700 }}>Ask Ellie</span></button>
    : <button style={{ ...styles.ellieFloat, ...(isMobile ? { bottom: 16, right: 16, padding: "10px 14px" } : {}), background: "#94a3b8" }} onClick={() => setShowUpgradeModal(true)}><span style={{ fontSize: isMobile ? 20 : 24 }}>🔒</span><span style={{ fontSize: 11, fontWeight: 700 }}>Ask Ellie</span></button>
  );
  const sharedAdvisor = showAdvisor && <Advisor accounts={accounts} allPayments={allPayments} onClose={() => setShowAdvisor(false)} />;
  const sharedModal = showModal && <EditModal
    editingAccount={editingAccount}
    form={form}
    setForm={setForm}
    errors={errors}
    setErrors={setErrors}
    closeModal={closeModal}
    handleSave={handleSave}
    notificationDaysBefore={editingAccount ? (notificationSettings[editingAccount.id] || []) : []}
    onNotificationChange={(days) => {
      const id = editingAccount?.id;
      if (!id) return;
      const updated = { ...notificationSettings };
      if (!days || (Array.isArray(days) && days.length === 0)) {
        delete updated[id];
      } else {
        updated[id] = Array.isArray(days) ? days : [days];
      }
      setNotificationSettings(updated);
      try { localStorage.setItem("elde_notifications", JSON.stringify(updated)); } catch {}
    }}
  />;
  const sharedBanner = newMilestones.length > 0 && effectiveIsPro && <MilestoneBanner newMilestones={newMilestones} onDismiss={() => setNewMilestones([])} />;

  // AUTH GATE — after all hooks
  if (showDeleteAccountModal) return (
    <div style={{ minHeight: "100vh", background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "32px 28px", maxWidth: 420, width: "100%" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#EF4444", marginBottom: 8 }}>🗑 Delete Account</div>
        <div style={{ fontSize: 14, color: "#555", marginBottom: 20, lineHeight: 1.6 }}>
          This will permanently delete your ELDE account and <strong>all your data</strong> including accounts, payment history, and milestones. This cannot be undone.
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 8 }}>Type <strong>DELETE</strong> to confirm:</div>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            style={{ width: "100%", border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "10px 14px", fontSize: 15, boxSizing: "border-box", outline: "none" }}
          />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => { setShowDeleteAccountModal(false); setDeleteConfirmText(""); }} style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#F3F4F6", color: "#555", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleDeleteAccount} disabled={deleteConfirmText !== "DELETE"} style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: "none", background: deleteConfirmText === "DELETE" ? "#EF4444" : "#FCA5A5", color: "#fff", fontWeight: 700, fontSize: 14, cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed" }}>Delete My Account</button>
        </div>
      </div>
    </div>
  );

  // Handle static page routes
  const path = window.location.pathname;
  if (path === "/privacy.html" || path === "/privacy") return <Privacy />;
  if (path === "/terms.html" || path === "/terms") return <Terms />;

  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: "#121220", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#4F7FFA", fontSize: 22, fontWeight: 800, letterSpacing: 2 }}>ELDE</div>
    </div>
  );
  if (!session && !authLoading) {
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    // Once a CTA is tapped, show Auth (with a back button to return to the intro).
    if (showAuth) return <Auth onBack={() => setShowAuth(false)} initialMode={authMode} />;
    // Native gets a pre-auth welcome intro; web keeps the full landing page.
    const onGetStarted = (mode = "signup") => { setAuthMode(mode); setShowAuth(true); };
    if (isNative) return <Welcome onGetStarted={onGetStarted} />;
    return <Landing onGetStarted={onGetStarted} />;
  }
  if (session && !dbLoaded) return (
    <div style={{ minHeight: "100vh", background: "#F8F9FC", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#1a1a2e", letterSpacing: 3 }}>ELDE</div>
      <div style={{ fontSize: 14, color: "#888" }}>Loading your data...</div>
    </div>
  );

  // First-run intro — only for brand-new users who haven't added a debt yet.
  if (!introSeen && accounts.length === 0) {
    return (
      <IntroScreen
        isMobile={isMobile}
        onAddDebt={() => { track("intro_cta", { action: "add_debt" }); dismissIntro(); openAddModal(); }}
        onSkip={() => { track("intro_cta", { action: "skip" }); dismissIntro(); }}
      />
    );
  }

  if (screen === "milestones") {
    if (!effectiveIsPro) return (<>{sharedNav}{sharedAdvisor}{sharedEllie}{sharedBanner}<div style={{ maxWidth: 600, margin: "60px auto", padding: "0 24px" }}><ProGate feature="milestones" onUpgrade={() => setShowUpgradeModal(true)} /></div>{sharedModal}{showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}</>);
    return (<>{sharedNav}{sharedAdvisor}{sharedEllie}{sharedBanner}<Milestones accounts={accounts} allPayments={allPayments} onBack={() => setScreen("dashboard")} monthlyPaymentsHistory={monthlyPaymentsHistory} />{sharedModal}{showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}</>);
  }

  if (screen === "details" && selectedAccount) {
    const idx = accounts.findIndex((a) => a.id === selectedAccount.id);
    const color = COLORS[idx % COLORS.length];
    return (
      <>
        {sharedNav}
        
        {sharedAdvisor}
        {sharedEllie}
        <AccountDetails
          account={selectedAccount}
          color={color}
          onBack={() => setScreen("dashboard")}
          onUpdateBalance={handleUpdateBalance}
          onUpdateLastPaymentDate={handleUpdateLastPaymentDate}
          onEdit={() => openEditModal(selectedAccount)}
          onDeletePayment={handleDeletePayment}
          onAddPayment={handleAddPayment}
          onMarkMonthPaid={handleMarkMonthPaid}
          allPayments={allPayments}
          easyMode={easyMode}
        />
        {sharedModal}
      </>
    );
  }

  if (screen === "calendar") {
    return (<>{sharedNav}{sharedAdvisor}{sharedEllie}{sharedBanner}<Calendar accounts={accounts} allPayments={allPayments} onBack={() => setScreen("dashboard")} />{sharedModal}{showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}</>);
  }

  if (screen === "amortization") {
    return (<>{sharedNav}{sharedAdvisor}{sharedEllie}{sharedBanner}<Amortization onBack={() => setScreen("dashboard")} />{sharedModal}{showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}</>);
  }

  if (screen === "calculators") {
    return (<>{sharedNav}{sharedAdvisor}{sharedEllie}{sharedBanner}<Calculators onBack={() => setScreen("dashboard")} />{sharedModal}{showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}</>);
  }

  if (screen === "strategy") {
    return (<>{sharedNav}{sharedAdvisor}{sharedEllie}{sharedBanner}<PayoffStrategy accounts={accounts} onBack={() => setScreen("dashboard")} customOrder={customOrder} onCustomOrderChange={(order) => { setCustomOrder(order); try { localStorage.setItem("elde_custom_order", JSON.stringify(order)); } catch {} }} activeStrategy={planSettings.strategy} onStrategyChange={(s) => setPlanSettings((p) => ({ ...p, strategy: s }))} onOverridesChange={(o) => setMonthOverrides(o)} isPro={effectiveIsPro} onUpgrade={() => setShowUpgradeModal(true)} />{sharedModal}{showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}</>);
  }

  return (
    <>
    <div style={styles.app}>
      {savedBanner && <div style={styles.savedBanner}>✓ Data saved</div>}
      {sharedBanner}

      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "env(safe-area-inset-top, 0px)", background: "#1a1a2e", zIndex: 9999 }} />
      <NavHeader screen={screen} setScreen={setScreen} hideBalances={hideBalances} setHideBalances={setHideBalances} accounts={accounts} openAddModal={openAddModal} onSignOut={handleSignOut} onDeleteAccount={() => setShowDeleteAccountModal(true)} isPro={effectiveIsPro} isAtFreeLimit={isAtFreeLimit} onUpgrade={() => setShowUpgradeModal(true)} onManageSubscription={handleManageSubscription} onRestorePurchases={handleRestorePurchases} onFeedback={handleFeedback} />

      <main style={{ ...styles.main, ...(isMobile ? { padding: "16px 12px", overflowX: "hidden" } : {}) }}>
        {/* Easy Mode Toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 16, gap: 10 }}>
          <span style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>Easy Mode</span>
          <div
            style={{ width: 44, height: 24, borderRadius: 12, background: easyMode ? "#10B981" : "#ddd", cursor: "pointer", position: "relative", transition: "background 0.2s" }}
            onClick={() => { const next = !easyMode; setEasyMode(next); try { localStorage.setItem("elde_easy_mode", String(next)); } catch {} }}
          >
            <div style={{ position: "absolute", top: 2, left: easyMode ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
          </div>
          {easyMode && <span style={{ fontSize: 11, background: "#D1FAE5", color: "#065F46", fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>ON</span>}
        </div>

        <AiInsightCards
          accounts={accounts}
          allPayments={allPayments}
          monthlyPayments={monthlyPayments}
          planSettings={planSettings}
          isPro={effectiveIsPro}
        />

        {!easyMode && <div style={{ ...styles.summaryGrid, ...(isMobile ? { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 8 } : {}) }}>
          <div style={{ ...styles.summaryCard, borderTop: "3px solid #EF4444", ...(isMobile ? { padding: "12px 14px" } : {}) }}>
            <div style={{ ...styles.summaryLabel, ...(isMobile ? { fontSize: 10 } : {}) }}>Total Debt</div>
            <div style={{ ...styles.summaryValue, color: "#EF4444", fontSize: isMobile ? 17 : 22, ...(isMobile ? {} : {}) }}>{hideBalances ? "••••••" : formatCurrency(totalDebt)}</div>
          </div>
          <div style={{ ...styles.summaryCard, borderTop: "3px solid #F97316", ...(isMobile ? { padding: "12px 14px" } : {}) }}>
            <div style={{ ...styles.summaryLabel, ...(isMobile ? { fontSize: 10 } : {}) }}>Monthly Minimums</div>
            <div style={{ ...styles.summaryValue, color: "#F97316", fontSize: isMobile ? 17 : 22 }}>{hideBalances ? "••••••" : formatCurrency(totalMin)}</div>
          </div>
          <div
            style={{ ...styles.summaryCard, borderTop: "3px solid #A855F7", cursor: "pointer", userSelect: "none", ...(isMobile ? { padding: "12px 14px" } : {}) }}
            onClick={() => setShowDailyInterest(d => !d)}
            title="Click to toggle between monthly and daily interest"
          >
            <div style={{ ...styles.summaryLabel, display: "flex", alignItems: "center", gap: 6, ...(isMobile ? { fontSize: 10 } : {}) }}>
              {showDailyInterest ? "Daily Interest" : "Monthly Interest"}
              <span style={{ fontSize: 9, background: "#A855F722", color: "#A855F7", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>tap</span>
            </div>
            <div style={{ ...styles.summaryValue, color: "#A855F7", fontSize: isMobile ? 17 : 22 }}>{hideBalances ? "••••••" : formatCurrency(showDailyInterest ? totalDailyInterest : totalMonthlyInterest)}</div>
          </div>
          <div
            style={{ ...styles.summaryCard, borderTop: paidCardMode === 0 ? "3px solid #4F7FFA" : paidCardMode === 1 ? "3px solid #10B981" : "3px solid #EF4444", cursor: "pointer", userSelect: "none", ...(isMobile ? { padding: "12px 14px" } : {}) }}
            onClick={() => setPaidCardMode(m => (m + 1) % 3)}
            title="Tap to cycle through Total Paid, Principal Paid, and Interest Paid"
          >
            <div style={{ ...styles.summaryLabel, display: "flex", alignItems: "center", gap: 6, ...(isMobile ? { fontSize: 10 } : {}) }}>
              {paidCardMode === 0 ? "Total Paid" : paidCardMode === 1 ? "Principal Paid" : "Interest Paid"}
              <span style={{ fontSize: 9, background: paidCardMode === 0 ? "#EEF2FF" : paidCardMode === 1 ? "#D1FAE5" : "#FEE2E2", color: paidCardMode === 0 ? "#4F7FFA" : paidCardMode === 1 ? "#10B981" : "#EF4444", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>tap</span>
            </div>
            <div style={{ ...styles.summaryValue, color: paidCardMode === 0 ? "#4F7FFA" : paidCardMode === 1 ? "#10B981" : "#EF4444", fontSize: isMobile ? 17 : 22 }}>
              {hideBalances ? "••••••" : formatCurrency(paidCardMode === 0 ? grandTotalPaid : paidCardMode === 1 ? grandTotalPrincipal : grandTotalPaid - grandTotalPrincipal)}
            </div>
          </div>
          <div style={{ ...styles.summaryCard, borderTop: "3px solid #10B981", ...(isMobile ? { padding: "12px 14px" } : {}) }}>
            <div style={{ ...styles.summaryLabel, display: "flex", alignItems: "center", gap: 6, ...(isMobile ? { fontSize: 10 } : {}) }}>
              Interest Saved
              <InfoBubble text="How much interest you've avoided so far by paying more than the minimum, compared to making only minimum payments on your accounts." />
            </div>
            <div style={{ ...styles.summaryValue, color: "#10B981", fontSize: isMobile ? 17 : 22 }}>{hideBalances ? "••••••" : formatCurrency(interestSavedActual)}</div>
          </div>
          {debtFreeDate && (
            <div
              style={{ ...styles.summaryCard, borderTop: "3px solid #00BCD4", cursor: "pointer", userSelect: "none", ...(isMobile ? { padding: "12px 14px" } : {}) }}
              onClick={() => setShowMonthsReduced(m => !m)}
              title="Tap to toggle between debt-free date and months reduced"
            >
              <div style={{ ...styles.summaryLabel, display: "flex", alignItems: "center", gap: 6, ...(isMobile ? { fontSize: 10 } : {}) }}>
                {showMonthsReduced ? "Months Reduced" : "Debt-Free Date"}
                <span style={{ fontSize: 9, background: "#00BCD422", color: "#00BCD4", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>tap</span>
                <InfoBubble text={showMonthsReduced
                  ? "How many months sooner you'll be debt-free thanks to the extra payments you've made, compared to a minimums-only schedule."
                  : "Your estimated debt-free date if you keep paying at least the minimum on every account."} />
              </div>
              <div style={{ ...styles.summaryValue, color: "#00BCD4", fontSize: isMobile ? 18 : 22 }}>
                {showMonthsReduced
                  ? `${monthsReducedActual} mo`
                  : debtFreeDate}
              </div>
            </div>
          )}
        </div>}

        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Your Debt Accounts</h2>
          <span style={styles.sectionCount}>{accounts.length} account{accounts.length !== 1 ? "s" : ""}</span>
        </div>

        {accounts.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📋</div>
            <div style={styles.emptyTitle}>No accounts yet</div>
            <div style={styles.emptyText}>Add your first debt account to get started on your payoff journey.</div>
            <button style={styles.addBtn} onClick={openAddModal}>+ Add Your First Account</button>
          </div>
        ) : (
          <div style={{ ...styles.accountGrid, ...(isMobile ? { gridTemplateColumns: "1fr", gap: 12 } : {}) }}>
            {accounts.map((acct, i) => {
              const color = COLORS[i % COLORS.length];
              const monthlyInterest = (acct.balance * (acct.rate / 100)) / 12;
              const daysUntil = getDaysUntilDue(acct.paymentDate);
              const badge = getDueBadge(daysUntil);
              return (
                <div key={acct.id} style={styles.accountCard}>
                  <div style={{ ...styles.accountColorBar, background: color }} />
                  <div style={styles.accountCardInner}>
                    <div style={styles.accountTop}>
                      <div>
                        <div style={styles.accountName}>{acct.name}</div>
                        <div style={{ ...styles.accountType, color }}>{acct.type}</div>
                      </div>
                      <div style={styles.cardActions}>
                        <button style={styles.editBtn} onClick={() => openEditModal(acct)} title="Edit account">✏️</button>
                        <button style={styles.deleteBtn} onClick={() => handleDelete(acct.id)} title="Remove account">✕</button>
                      </div>
                    </div>
                    {badge && (() => {
                      const nextDue = getNextDue(acct.paymentDate, acct.lastPaymentDate);
                      return (
                        <div style={{ ...styles.dueBadge, color: badge.color, background: badge.bg }}>
                          📅 {badge.label} {acct.paymentDate ? `(${nextDue.toLocaleDateString("en-US", { month: "short", day: "numeric" })})` : ""}
                        </div>
                      );
                    })()}
                    <div style={styles.balanceRow}>
                      <span style={styles.balanceLabel}>Balance</span>
                      <span style={styles.balanceValue}>{hideBalances ? "••••••" : formatCurrency(acct.balance)}</span>
                    </div>
                    <div style={styles.progressBg}>
                      {(() => {
                        const acctPayments = allPayments[acct.id] || [];
                        const totalPrincipalPaid = acctPayments.reduce((s, p) => s + (p.principal || 0), 0);
                        const originalBalance = acct.balance + totalPrincipalPaid;
                        const progress = originalBalance > 0 ? Math.min((totalPrincipalPaid / originalBalance) * 100, 100) : 0;
                        return <div style={{ ...styles.progressFill, background: color, width: `${progress}%` }} />;
                      })()}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#999", marginBottom: 8, marginTop: 2 }}>
                      <span>{(() => {
                        const acctPayments = allPayments[acct.id] || [];
                        const totalPrincipalPaid = acctPayments.reduce((s, p) => s + (p.principal || 0), 0);
                        const originalBalance = acct.balance + totalPrincipalPaid;
                        const progress = originalBalance > 0 ? Math.min((totalPrincipalPaid / originalBalance) * 100, 100) : 0;
                        return `${progress.toFixed(1)}% paid off`;
                      })()}</span>
                      <span>{(() => {
                        const acctPayments = allPayments[acct.id] || [];
                        const totalPrincipalPaid = acctPayments.reduce((s, p) => s + (p.principal || 0), 0);
                        const originalBalance = acct.balance + totalPrincipalPaid;
                        return hideBalances ? "••••••" : `${formatCurrency(totalPrincipalPaid)} of ${formatCurrency(originalBalance)}`;
                      })()}</span>
                    </div>
                    {easyMode ? (
                      <div style={styles.accountStats}>
                        <div style={styles.statItem}>
                          <div style={styles.statLabel}>Min. Payment</div>
                          <div style={styles.statValue}>{hideBalances ? "••" : formatCurrency(acct.minPayment)}/mo</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statLabel}>Monthly Interest</div>
                          <div style={{ ...styles.statValue, color: "#EF4444" }}>{hideBalances ? "••" : formatCurrency(monthlyInterest)}</div>
                        </div>
                        {acct.paymentDate && (() => {
                          const nextDue = getNextDue(acct.paymentDate, acct.lastPaymentDate);
                          return (
                            <div style={styles.statItem}>
                              <div style={styles.statLabel}>Due Date</div>
                              <div style={styles.statValue}>{nextDue.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div style={styles.accountStats}>
                        <div style={styles.statItem}>
                          <div style={styles.statLabel}>Interest Rate</div>
                          <div style={styles.statValue}>{acct.rate}%</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statLabel}>Min. Payment</div>
                          <div style={styles.statValue}>{hideBalances ? "••" : formatCurrency(acct.minPayment)}/mo</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statLabel}>Monthly Interest</div>
                          <div style={{ ...styles.statValue, color: "#EF4444" }}>{hideBalances ? "••" : formatCurrency(monthlyInterest)}</div>
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                      <button
                        style={{ ...styles.detailsBtn, borderColor: color, color, flex: 2 }}
                        onClick={() => { setSelectedAccount(acct); setScreen("details"); }}
                      >
                        View Details →
                      </button>
                      <button
                        style={{ ...styles.detailsBtn, borderColor: "#F59E0B", color: "#F59E0B", flex: 1, whiteSpace: "nowrap" }}
                        onClick={() => openExtraPay(acct)}
                        title="Make a one-time extra payment"
                      >
                        💸 Extra
                      </button>
                    </div>
                    {!easyMode && (() => {
                      const impact = calcExtraPaymentImpact(acct.balance, acct.rate, acct.minPayment);
                      if (!impact) return null;
                      return (
                        <div style={styles.impactMsg}>
                          💡 Paying an extra <strong>{formatCurrency(impact.extra)}/mo</strong> saves <strong style={{ color: "#10B981" }}>{formatCurrency(impact.interestSaved)}</strong> in interest and pays off <strong style={{ color: "#10B981" }}>{impact.monthsSaved} month{impact.monthsSaved !== 1 ? "s" : ""} sooner!</strong>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Payment Plan Section */}
        {accounts.length > 0 && (
          <div style={{ ...styles.planSection, ...(isMobile ? { padding: "16px 10px", overflow: "hidden" } : {}) }}>
            <div style={{ ...styles.planHeader, ...(isMobile ? { flexDirection: "column", gap: 12 } : {}) }}>
              <div>
                <h2 style={styles.sectionTitle}>
                  💳 Payment Plan —{" "}
                  {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </h2>
                <div style={styles.planSubtitle}>
                  {(() => {
                    const dueAccounts = accounts.filter(isDueThisMonth);
                    const paidCount = Object.keys(monthlyPayments.paid).filter(id => dueAccounts.find(a => String(a.id) === String(id))).length;
                    return `${paidCount} of ${dueAccounts.length} payment${dueAccounts.length !== 1 ? "s" : ""} made this month`;
                  })()}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* Progress bar */}
                <div style={styles.planProgress}>
                <div style={styles.planProgressBg}>
                  <div style={{
                    ...styles.planProgressFill,
                    width: `${(() => {
                      const dueAccounts = accounts.filter(isDueThisMonth);
                      const paidCount = Object.keys(monthlyPayments.paid).filter(id => dueAccounts.find(a => String(a.id) === String(id))).length;
                      return Math.min(dueAccounts.length > 0 ? (paidCount / dueAccounts.length) * 100 : 0, 100);
                    })()}%`,
                  }} />
                </div>
                <div style={styles.planProgressLabel}>
                  {(() => {
                    const dueAccounts = accounts.filter(isDueThisMonth);
                    const paidCount = Object.keys(monthlyPayments.paid).filter(id => dueAccounts.find(a => String(a.id) === String(id))).length;
                    return `${Math.min(Math.round(dueAccounts.length > 0 ? (paidCount / dueAccounts.length) * 100 : 0), 100)}%`;
                  })()}
                </div>
              </div>
              {paymentPlan.filter(isDueThisMonth).some(acct => !monthlyPayments.paid[acct.id]) && (
                <button
                  onClick={() => { setSelectionMode(s => !s); setSelectedForPay([]); }}
                  style={{ background: selectionMode ? "#EEF2FF" : "#F3F4F6", color: selectionMode ? "#4F7FFA" : "#555", border: selectionMode ? "1.5px solid #4F7FFA" : "1.5px solid #E5E7EB", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {selectionMode ? "✕ Cancel" : "Select"}
                </button>
              )}
              </div>
            </div>
            <div style={{ ...styles.planControls, ...(isMobile ? { flexDirection: "column", gap: 16 } : {}) }}>
              <div style={styles.planControlGroup}>
                <div style={styles.planControlLabel}>Strategy</div>
                <div style={{ ...styles.strategyTabs, ...(isMobile ? { flexWrap: "wrap" } : {}) }}>
                  {[
                    { key: "avalanche", label: "🏔 Avalanche", desc: "Highest interest first" },
                    { key: "snowball", label: "⛄ Snowball", desc: "Smallest balance first" },
                    { key: "custom", label: "🎯 Custom", desc: "Current order" },
                  ].map((s) => (
                    <button
                      key={s.key}
                      style={{ ...styles.strategyTab, ...(planSettings.strategy === s.key ? styles.strategyTabActive : {}) }}
                      onClick={() => { setPlanSettings((p) => ({ ...p, strategy: s.key })); try { localStorage.setItem("elde_strategy", s.key); } catch {} }}
                      title={s.desc}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={styles.planControlGroup}>
                <div style={styles.planControlLabel}>Extra Payment Amount</div>
                <div style={styles.extraInputRow}>
                  <span style={styles.extraPrefix}>$</span>
                  <input
                    style={{ ...styles.extraInput, ...(effectiveExtraBudget !== planSettings.extraBudget ? { borderColor: "#F59E0B", color: "#F59E0B" } : {}) }}
                    type="number"
                    min="0"
                    value={planSettings.extraBudget}
                    onChange={(e) => { const v = Math.max(0, Number(e.target.value)); setPlanSettings((p) => ({ ...p, extraBudget: v })); }}
                  />
                  <span style={styles.extraSuffix}>/mo</span>
                </div>
                {effectiveExtraBudget !== planSettings.extraBudget && (
                  <div style={{ fontSize: 11, color: "#F59E0B", marginTop: 4, fontWeight: 600 }}>
                    📅 This month: {formatCurrency(effectiveExtraBudget)} (scheduled override)
                  </div>
                )}
              </div>
            </div>
            {/* Savings Insight Banner */}
            {(() => {
              const savings = calcSavings(accounts, planSettings.strategy, effectiveExtraBudget, customOrder);
              if (!savings) return null;
              const payoffDate = new Date();
              payoffDate.setMonth(payoffDate.getMonth() + savings.payoffMonthsWithExtra);
              const payoffStr = payoffDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
              return (
                <div style={styles.savingsBanner}>
                  <div style={styles.savingsBannerIcon}>💡</div>
                  <div style={styles.savingsBannerText}>
                    By paying an extra <strong style={{ color: "#10B981" }}>{formatCurrency(effectiveExtraBudget)}/mo</strong> you'll save{" "}
                    <strong style={{ color: "#10B981" }}>{formatCurrency(savings.interestSaved)}</strong> in interest and pay off all debt{" "}
                    <strong style={{ color: "#10B981" }}>{savings.monthsSaved} month{savings.monthsSaved !== 1 ? "s" : ""} sooner</strong>
                    {" "}— by <strong style={{ color: "#4F7FFA" }}>{payoffStr}</strong> instead of{" "}
                    {(() => {
                      const minDate = new Date();
                      minDate.setMonth(minDate.getMonth() + savings.payoffMonthsWithMin);
                      return minDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                    })()}.
                  </div>
                </div>
              );
            })()}

            {selectionMode && selectedForPay.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#EEF2FF", border: "1.5px solid #C7D2FE", borderRadius: 10, padding: "12px 16px", marginBottom: 12 }}>
                <div style={{ fontSize: 14, color: "#4338CA", fontWeight: 600 }}>{selectedForPay.length} account{selectedForPay.length !== 1 ? "s" : ""} selected</div>
                <button
                  onClick={handleMarkAllPaid}
                  style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                >
                  ✓ Mark {selectedForPay.length} Paid
                </button>
              </div>
            )}
            {bulkPaidToast && (
              <div style={{ background: "#D1FAE5", border: "1px solid #6EE7B7", borderRadius: 10, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#065F46", fontWeight: 600 }}>
                <span style={{ fontSize: 18 }}>✅</span>
                {bulkPaidToast} payment{bulkPaidToast !== 1 ? "s" : ""} marked as paid for today!
              </div>
            )}
            <div style={styles.planRows}>
              {paymentPlan.filter(isDueThisMonth).length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 20px", background: "#F8F9FC", borderRadius: 12 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🎉</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", marginBottom: 6 }}>No payments due this month</div>
                  <div style={{ fontSize: 13, color: "#888" }}>Your next payments will appear here when they're due.</div>
                </div>
              ) : (
              paymentPlan
                .filter(isDueThisMonth)
                .sort((a, b) => a.rank - b.rank).map((acct) => {
                const colorIdx = accounts.findIndex((a) => a.id === acct.id);
                const color = COLORS[colorIdx % COLORS.length];
                const isPaid = !!monthlyPayments.paid[acct.id];
                const paidDate = monthlyPayments.paid[acct.id];
                return (
                  <div key={acct.id} style={{
                    ...styles.planRow,
                    borderLeft: `4px solid ${isPaid ? "#10B981" : acct.isFocus ? color : "#eee"}`,
                    opacity: isPaid ? 0.75 : 1,
                    cursor: selectionMode && !isPaid ? "pointer" : "default",
                    outline: selectionMode && selectedForPay.includes(acct.id) ? "2px solid #4F7FFA" : "none",
                    ...(isMobile ? { flexDirection: "column", alignItems: "flex-start", gap: 10, padding: "12px 10px" } : {}),
                  }} onClick={() => {
                    if (!selectionMode || isPaid) return;
                    setSelectedForPay(prev => prev.includes(acct.id) ? prev.filter(id => id !== acct.id) : [...prev, acct.id]);
                  }}>
                    <div style={{ ...styles.planRowLeft, ...(isMobile ? { width: "100%" } : {}) }}>
                      {selectionMode && !isPaid && (
                        <div style={{
                          width: 20, height: 20, borderRadius: 5, border: `2px solid ${selectedForPay.includes(acct.id) ? "#4F7FFA" : "#CBD5E1"}`,
                          background: selectedForPay.includes(acct.id) ? "#4F7FFA" : "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          {selectedForPay.includes(acct.id) && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>}
                        </div>
                      )}
                      <div style={{ ...styles.planRank, background: isPaid ? "#10B981" : acct.isFocus ? color : "#e5e7eb", color: isPaid || acct.isFocus ? "#fff" : "#888" }}>
                        {isPaid ? "✓" : acct.rank}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...styles.planAccountName, textDecoration: isPaid ? "line-through" : "none", color: isPaid ? "#888" : "#1a1a2e" }}>{acct.name}</div>
                        <div style={styles.planAccountMeta}>{acct.type} · {acct.rate}% APR</div>
                      </div>
                      {!isPaid && acct.isFocus && <div style={styles.focusBadge}>🎯 Focus debt</div>}
                      {isPaid && <div style={styles.paidBadge}>✓ Paid {paidDate}</div>}
                    </div>
                    <div style={{ ...styles.planRowRight, ...(isMobile ? { width: "100%", flexDirection: "column", alignItems: "stretch", gap: 8 } : {}) }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 }}>
                        <div style={styles.planPaymentBreakdown}>
                          <div style={styles.planPaymentItem}>
                            <span style={styles.planPaymentLabel}>Minimum</span>
                            <span style={styles.planPaymentValue}>{hideBalances ? "••" : formatCurrency(acct.minPayment)}</span>
                          </div>
                          {acct.extra > 0 && (
                            <div style={styles.planPaymentItem}>
                              <span style={styles.planPaymentLabel}>Extra</span>
                              <span style={{ ...styles.planPaymentValue, color: "#10B981" }}>+ {hideBalances ? "••" : formatCurrency(acct.extra)}</span>
                            </div>
                          )}
                        </div>
                        <div style={{ ...styles.planTotal, color: isPaid ? "#10B981" : color }}>
                          {hideBalances ? "••••••" : (() => {
                            if (isPaid) {
                              const key = getCurrentMonthKey();
                              const [yr, mo] = key.split("-").map(Number);
                              const logged = (allPayments[acct.id] || []).filter(p => {
                                const d = new Date(p.date + "T00:00:00");
                                return d.getFullYear() === yr && d.getMonth() === mo - 1;
                              });
                              const loggedTotal = logged.reduce((s, p) => s + p.amount, 0);
                              return formatCurrency(loggedTotal || acct.totalPayment);
                            }
                            return formatCurrency(acct.totalPayment);
                          })()}
                          <div style={styles.planTotalLabel}>{isPaid ? "paid" : "this month"}</div>
                        </div>
                        {!isMobile && (
                          <button
                            style={{ ...styles.markPaidBtn, background: isPaid ? "#F3F4F6" : "#10B981", color: isPaid ? "#888" : "#fff", marginLeft: 16 }}
                            onClick={() => isPaid ? toggleMonthlyPaid(acct.id) : openQuickPay(acct, colorIdx)}
                          >
                            {isPaid ? "Undo" : "Log & Mark Paid"}
                          </button>
                        )}
                      </div>
                      {isMobile && (
                        <button
                          style={{ ...styles.markPaidBtn, background: isPaid ? "#F3F4F6" : "#10B981", color: isPaid ? "#888" : "#fff", width: "100%", padding: "10px", fontSize: 13 }}
                          onClick={() => isPaid ? toggleMonthlyPaid(acct.id) : openQuickPay(acct, colorIdx)}
                        >
                          {isPaid ? "✓ Undo" : "Log & Mark Paid"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }))}
            </div>
            {paymentPlan.filter(isDueThisMonth).length > 0 && (
            <div style={styles.planSummary}>
              <div style={styles.planSummaryItem}>
                <span style={styles.planSummaryLabel}>Total minimums</span>
                <span style={styles.planSummaryValue}>{hideBalances ? "••••••" : formatCurrency(totalMin)}</span>
              </div>
              {effectiveExtraBudget > 0 && (
                <div style={styles.planSummaryItem}>
                  <span style={styles.planSummaryLabel}>Extra payment{effectiveExtraBudget !== planSettings.extraBudget ? " (this month)" : ""}</span>
                  <span style={{ ...styles.planSummaryValue, color: "#10B981" }}>+ {hideBalances ? "••••••" : formatCurrency(effectiveExtraBudget)}</span>
                </div>
              )}
              <div style={{ ...styles.planSummaryItem, borderTop: "2px solid #e5e7eb", paddingTop: 10, marginTop: 4 }}>
                <span style={{ ...styles.planSummaryLabel, fontWeight: 700, color: "#1a1a2e" }}>Total this month</span>
                <span style={{ ...styles.planSummaryValue, fontSize: 20, color: "#4F7FFA" }}>{hideBalances ? "••••••" : formatCurrency(totalMin + effectiveExtraBudget)}</span>
              </div>
            </div>
            )}

            {/* Option C: What if I pay extra this month? */}
            {accounts.length > 0 && (() => {

              // Simulate full payoff across all accounts with avalanche/snowball strategy
              function simulatePayoff(accts, extraOneTime = 0) {
                if (!accts.length) return { months: 0, totalInterest: 0 };
                // Sort by strategy
                let ordered;
                if (planSettings.strategy === "avalanche") ordered = [...accts].sort((a, b) => b.rate - a.rate);
                else if (planSettings.strategy === "snowball") ordered = [...accts].sort((a, b) => a.balance - b.balance);
                else ordered = [...accts];

                let debts = ordered.map(a => ({ ...a, remaining: a.balance }));
                const totalMin = debts.reduce((s, d) => s + d.minPayment, 0);
                const monthlyBudget = totalMin + planSettings.extraBudget;
                let totalInterest = 0;
                let month = 0;

                // Apply one-time extra to focus debt first
                if (extraOneTime > 0) {
                  let remaining = extraOneTime;
                  for (let d of debts) {
                    if (d.remaining <= 0) continue;
                    const apply = Math.min(remaining, d.remaining);
                    d.remaining -= apply;
                    remaining -= apply;
                    if (remaining <= 0) break;
                  }
                }

                while (debts.some(d => d.remaining > 0) && month < 600) {
                  month++;
                  let budgetLeft = monthlyBudget;
                  // Pay minimums
                  for (let d of debts) {
                    if (d.remaining <= 0) continue;
                    const interest = d.remaining * (d.rate / 100 / 12);
                    totalInterest += interest;
                    const pay = Math.min(d.minPayment, d.remaining + interest);
                    d.remaining = Math.max(d.remaining - (pay - interest), 0);
                    budgetLeft -= pay;
                  }
                  // Apply extra to focus debt
                  for (let d of debts) {
                    if (d.remaining <= 0 || budgetLeft <= 0) continue;
                    const extra = Math.min(budgetLeft, d.remaining);
                    d.remaining = Math.max(d.remaining - extra, 0);
                    budgetLeft -= extra;
                  }
                }
                return { months: month, totalInterest };
              }

              const base = simulatePayoff(accounts, 0);
              const withExtra = simulatePayoff(accounts, whatIfExtra);
              const monthsImpact = Math.max(base.months - withExtra.months, 0);
              const interestSavedAmt = Math.max(base.totalInterest - withExtra.totalInterest, 0);
              return (
                <div style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A", borderRadius: 12, padding: "16px 18px", marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>💸 What if I pay extra this month?</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: whatIfExtra > 0 ? 10 : 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#F59E0B" }}>$</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Enter amount"
                      value={whatIfExtra || ""}
                      onChange={e => setWhatIfExtra(Math.max(0, Number(e.target.value)))}
                      style={{ border: "1.5px solid #FDE68A", borderRadius: 8, padding: "8px 10px", fontSize: 15, fontWeight: 700, width: 120, outline: "none", background: "#fff", color: "#92400E" }}
                    />
                    <span style={{ fontSize: 13, color: "#92400E" }}>extra one-time</span>
                    {whatIfExtra > 0 && (
                      <button onClick={() => { openExtraPay(accounts[0]); setExtraPayAmount(String(whatIfExtra)); }} style={{ marginLeft: "auto", background: "#F59E0B", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        Apply →
                      </button>
                    )}
                  </div>
                  {whatIfExtra > 0 && (monthsImpact > 0 || interestSavedAmt > 0) && (
                    <div style={{ fontSize: 13, color: "#92400E", background: "#FEF3C7", borderRadius: 8, padding: "8px 12px", lineHeight: 1.6 }}>
                      Saves <strong style={{ color: "#10B981" }}>{formatCurrency(interestSavedAmt)}</strong> in interest
                      {monthsImpact > 0 && <> and pays off debt <strong style={{ color: "#10B981" }}>{monthsImpact} month{monthsImpact !== 1 ? "s" : ""} sooner</strong></>}!
                    </div>
                  )}
                  {whatIfExtra > 0 && monthsImpact === 0 && interestSavedAmt === 0 && (
                    <div style={{ fontSize: 12, color: "#92400E" }}>Enter an amount to see the impact on your payoff timeline.</div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Extra Payment Modal */}
        {extraPayModal && (() => {
          const { acct } = extraPayModal;
          const amount = parseFloat(extraPayAmount) || 0;
          const today = new Date().toISOString().split("T")[0];
          const days = getDaysBetween(acct.lastPaymentDate, today);
          const { interest, principal } = amount > 0 ? calcSplit(amount, acct.balance, acct.rate, days) : { interest: 0, principal: 0 };
          const r = acct.rate / 100 / 12;
          const baseMonths = acct.minPayment > acct.balance * r ? Math.ceil(Math.log(acct.minPayment / (acct.minPayment - acct.balance * r)) / Math.log(1 + r)) : null;
          const newBalance = Math.max(acct.balance - principal, 0);
          const newMonths = newBalance > 0 && acct.minPayment > newBalance * r ? Math.ceil(Math.log(acct.minPayment / (acct.minPayment - newBalance * r)) / Math.log(1 + r)) : 0;
          const monthsSaved = baseMonths && newMonths ? Math.max(baseMonths - newMonths, 0) : 0;
          const baseTotalInterest = baseMonths ? baseMonths * acct.minPayment - acct.balance : 0;
          const newTotalInterest = newMonths ? newMonths * acct.minPayment - newBalance : 0;
          const interestSaved = Math.max(baseTotalInterest - newTotalInterest, 0);
          const colorIdx = accounts.findIndex(a => a.id === acct.id);
          const color = COLORS[colorIdx % COLORS.length];
          return (
            <div style={{ ...styles.modalOverlay, ...(isMobile ? { alignItems: "flex-end", padding: 0 } : {}) }} onClick={() => setExtraPayModal(null)}>
              <div style={{ ...styles.modal, maxWidth: isMobile ? "100%" : 420, ...(isMobile ? { borderRadius: "16px 16px 0 0", maxHeight: "80vh" } : {}) }} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>💸 Extra Payment — {acct.name}</h3>
                  <button style={styles.modalClose} onClick={() => setExtraPayModal(null)}>✕</button>
                </div>
                <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color }}>$</span>
                    <input
                      autoFocus
                      type="number"
                      min="0"
                      placeholder="0.00"
                      value={extraPayAmount}
                      onChange={e => setExtraPayAmount(e.target.value)}
                      style={{ ...styles.input, fontSize: 24, fontWeight: 800, color, borderColor: color, flex: 1 }}
                    />
                  </div>
                  {amount > 0 && (
                    <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span style={{ color: "#888" }}>Principal paid</span>
                        <span style={{ fontWeight: 700, color: "#10B981" }}>{formatCurrency(principal)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span style={{ color: "#888" }}>Interest</span>
                        <span style={{ fontWeight: 700, color: "#EF4444" }}>{formatCurrency(interest)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderTop: "1px solid #BBF7D0", paddingTop: 8 }}>
                        <span style={{ color: "#888" }}>New balance</span>
                        <span style={{ fontWeight: 700 }}>{formatCurrency(newBalance)}</span>
                      </div>
                      {(monthsSaved > 0 || interestSaved > 0) && (
                        <div style={{ background: "#DCFCE7", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#166534", fontWeight: 600, textAlign: "center" }}>
                          🎉 Saves {formatCurrency(interestSaved)} in interest{monthsSaved > 0 ? ` and pays off debt ${monthsSaved} month${monthsSaved !== 1 ? "s" : ""} sooner` : ""}!
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button style={styles.cancelBtn} onClick={() => setExtraPayModal(null)}>Cancel</button>
                    <button
                      style={{ ...styles.addBtn, flex: 1, opacity: amount > 0 ? 1 : 0.5 }}
                      disabled={amount <= 0}
                      onClick={handleExtraPay}
                    >
                      Log Extra Payment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Transaction History Section */}
        {accounts.length > 0 && !easyMode && (
          <TransactionHistory
            accounts={accounts}
            allPayments={allPayments}
            hideBalances={hideBalances}
            onAccountClick={(acct) => { setSelectedAccount(acct); setScreen("details"); }}
            onAmountClick={(txn, acct) => setTxnPopup({ txn, acct })}
          />
        )}

        {/* Transaction Interest Popup */}
        {txnPopup && (() => {
            const { txn, acct } = txnPopup;
            if (!acct) return null;
            const payDate = new Date(txn.date + "T00:00:00");
            const colorIdx = accounts.findIndex(a => a.id === acct.id);
            const color = ["#4F7FFA","#F97316","#10B981","#A855F7","#EF4444","#F59E0B"][colorIdx % 6];
            const acctPayments = (allPayments[acct.id] || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date));
            const txnIdx = acctPayments.findIndex(p => p.id === txn.id);
            const prevPayment = txnIdx >= 0 ? acctPayments[txnIdx + 1] : null;
            const prevDate = prevPayment ? prevPayment.date : (acct.previousLastPaymentDate || acct.originalLastPaymentDate || null);
            const prevDateObj = prevDate ? new Date(prevDate + "T00:00:00") : null;
            const days = prevDateObj ? Math.max(Math.ceil((payDate - prevDateObj) / (1000 * 60 * 60 * 24)), 1) : null;
            const balanceAtTime = acct.balance + (acctPayments
              .filter(p => new Date(p.date + "T00:00:00") >= payDate)
              .reduce((s, p) => s + (p.principal || 0), 0));
            return (
              <div style={{ ...styles.modalOverlay, ...(isMobile ? { alignItems: "flex-end", padding: 0 } : {}) }} onClick={() => setTxnPopup(null)}>
                <div style={{ ...styles.modal, maxWidth: isMobile ? "100%" : 400, ...(isMobile ? { borderRadius: "16px 16px 0 0", maxHeight: "80vh" } : {}) }} onClick={e => e.stopPropagation()}>
                  <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Interest Calculation</h3>
                    <button style={styles.modalClose} onClick={() => setTxnPopup(null)}>✕</button>
                  </div>
                  <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 0 }}>
                    <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
                      Payment on {payDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </div>
                    <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>How interest was calculated</div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                        <span>Balance at payment time</span><span style={{ fontWeight: 600 }}>{formatCurrency(balanceAtTime)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                        <span>× Annual rate</span><span style={{ fontWeight: 600 }}>{acct.rate}%</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                        <span>÷ 365 days</span><span style={{ fontWeight: 600 }}>= {formatCurrency(balanceAtTime * acct.rate / 100 / 365)}/day</span>
                      </div>
                      {days && (
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                          <span>× <strong>{days}</strong> days since last payment</span>
                          <span style={{ fontWeight: 600 }}>
                            {prevDate ? `(${new Date(prevDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} → ${payDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })})` : ""}
                          </span>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0 2px", fontWeight: 700 }}>
                        <span>= Interest charged</span><span style={{ color: "#EF4444" }}>{formatCurrency(txn.interest)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#F0FDF4", borderRadius: 10, padding: "14px 16px", border: "1px solid #BBF7D0" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Payment breakdown</div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                        <span>Total payment</span><span style={{ fontWeight: 600 }}>{formatCurrency(txn.amount)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                        <span>− Interest</span><span style={{ fontWeight: 600, color: "#EF4444" }}>− {formatCurrency(txn.interest)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                        <span>− Fees</span><span style={{ fontWeight: 600, color: "#F59E0B" }}>− {formatCurrency(txn.fees || 0)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0 2px", fontWeight: 700 }}>
                        <span>= Principal paid</span><span style={{ color }}>{formatCurrency(txn.principal)}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end" }}>
                    <button style={styles.cancelBtn} onClick={() => setTxnPopup(null)}>Close</button>
                  </div>
                </div>
              </div>
            );
          })()}
      </main>

      {showModal && <EditModal editingAccount={editingAccount} form={form} setForm={setForm} errors={errors} setErrors={setErrors} closeModal={closeModal} handleSave={handleSave} notificationDaysBefore={editingAccount ? (notificationSettings[editingAccount.id] || []) : pendingReminders} onNotificationChange={(days) => { const id = editingAccount?.id; if (!id) { setPendingReminders(days && days.length ? days : []); return; } const updated = { ...notificationSettings }; if (!days || (Array.isArray(days) && days.length === 0)) { delete updated[id]; } else { updated[id] = Array.isArray(days) ? days : [days]; } setNotificationSettings(updated); try { localStorage.setItem("elde_notifications", JSON.stringify(updated)); } catch {}; }} />}

      {showAdvisor && (
        <Advisor
          accounts={accounts}
          allPayments={allPayments}
          onClose={() => setShowAdvisor(false)}
        />
      )}

      {/* Floating Ellie Button — Pro-gated (shows lock + upgrade for non-Pro) */}
      {sharedEllie}


      {/* Quick Pay Modal */}
      {quickPay && quickPayForm && (() => {
        const { account, colorIdx } = quickPay;
        const color = COLORS[colorIdx % COLORS.length]; // eslint-disable-line no-unused-vars
        const formDays = getDaysBetween(account.lastPaymentDate, quickPayForm.date);
        const PAYMENT_TYPES = ["Minimum Payment", "Extra Payment", "Full Payoff", "Partial Payment", "Other"];
        return (
          <div style={{ ...styles.modalOverlay, ...(isMobile ? { alignItems: "flex-end", padding: 0 } : {}) }} onClick={() => { setQuickPay(null); setQuickPayForm(null); }}>
            <div style={{ ...styles.modal, ...(isMobile ? { maxWidth: "100%", maxHeight: "92vh", borderRadius: "16px 16px 0 0" } : {}) }} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Log Payment — {account.name}</h3>
                <button style={styles.modalClose} onClick={() => { setQuickPay(null); setQuickPayForm(null); }}>✕</button>
              </div>
              <div style={styles.modalBody}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Amount ($)</label>
                  <input
                    style={styles.input} type="number" min="0"
                    value={quickPayForm.amount}
                    onChange={(e) => handleQuickPayAmountChange(e.target.value)}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Date</label>
                  <input
                    style={styles.input} type="date"
                    value={quickPayForm.date}
                    onChange={(e) => handleQuickPayDateChange(e.target.value)}
                  />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Principal Paid ($)</label>
                    <input
                      style={styles.input} type="number" min="0"
                      value={quickPayForm.principal}
                      onChange={(e) => setQuickPayForm((f) => ({ ...f, principal: e.target.value }))}
                      onBlur={(e) => {
                        const principal = Math.max(parseFloat(e.target.value) || 0, 0);
                        const interest = parseFloat(quickPayForm.interest) || 0;
                        const fees = parseFloat(quickPayForm.fees) || 0;
                        const newAmount = (principal + interest + fees).toFixed(2);
                        setQuickPayForm((f) => ({ ...f, principal: principal.toFixed(2), amount: newAmount }));
                      }}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Interest Paid ($)</label>
                    <input
                      style={styles.input} type="number" min="0"
                      value={quickPayForm.interest}
                      onChange={(e) => setQuickPayForm((f) => ({ ...f, interest: e.target.value }))}
                      onBlur={(e) => {
                        const amt = parseFloat(quickPayForm.amount) || 0;
                        const fees = parseFloat(quickPayForm.fees) || 0;
                        const available = Math.max(amt - fees, 0);
                        const val = Math.min(parseFloat(e.target.value) || 0, available);
                        setQuickPayForm((f) => ({ ...f, interest: val.toFixed(2), principal: Math.max(available - val, 0).toFixed(2) }));
                      }}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#888", background: "#F8F9FC", padding: "8px 12px", borderRadius: 8, lineHeight: 1.6 }}>
                  💡 Estimated based on <strong>{formDays} days</strong> since last payment. Adjust to match your statement if needed.
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Fees ($) <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></label>
                  <input
                    style={styles.input} type="number" min="0" placeholder="0.00"
                    value={quickPayForm.fees}
                    onChange={(e) => {
                      const fees = parseFloat(e.target.value) || 0;
                      const amt = parseFloat(quickPayForm.amount) || 0;
                      const available = Math.max(amt - fees, 0);
                      const interest = Math.min(parseFloat(quickPayForm.interest) || 0, available);
                      const principal = Math.max(available - interest, 0);
                      setQuickPayForm((f) => ({ ...f, fees: e.target.value, principal: principal.toFixed(2), interest: interest.toFixed(2) }));
                    }}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Type</label>
                  <select style={styles.input} value={quickPayForm.type} onChange={(e) => setQuickPayForm((f) => ({ ...f, type: e.target.value }))}>
                    {PAYMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes (optional)</label>
                  <input
                    style={styles.input} type="text" placeholder="e.g. March payment"
                    value={quickPayForm.notes}
                    onChange={(e) => setQuickPayForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button style={styles.cancelBtn} onClick={() => { setQuickPay(null); setQuickPayForm(null); }}>Cancel</button>
                <button style={{ ...styles.addBtn, background: "#10B981" }} onClick={handleQuickPaySubmit}>
                  ✓ Log & Mark Paid
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
    {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onUpgrade={handleUpgrade} checkoutLoading={checkoutLoading} />}
    </>
  );
}

const styles = {
  app: { minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" },
  savedBanner: { position: "fixed", bottom: 24, right: 24, background: "#10B981", color: "#fff", padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 },
  logoBox: { display: "flex", alignItems: "center", border: "2.5px solid #00BCD4", borderRadius: 4, padding: "2px 8px", background: "#000" },
  logoText: { fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: 2 },
  logoTextAccent: { fontSize: 22, fontWeight: 900, color: "#00BCD4", letterSpacing: 2 },

  headerActions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  addBtn: { background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  feedbackFloat: { position: "fixed", bottom: 120, right: 28, zIndex: 2500 },
  ellieFloat: { position: "fixed", bottom: 28, right: 28, zIndex: 2000, background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 50, padding: "14px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer", boxShadow: "0 4px 20px rgba(79,127,250,0.5)" },
  calendarBtn: { background: "#1e2d4a", color: "#fff", border: "1px solid #F59E0B", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  amortBtn: { background: "#1e2d4a", color: "#fff", border: "1px solid #A855F7", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  calcBtn: { background: "#1e2d4a", color: "#fff", border: "1px solid #10B981", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  strategyBtn: { background: "#1e2d4a", color: "#fff", border: "1px solid #4F7FFA", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
  hideBtn: { background: "transparent", color: "#8899BB", border: "1px solid #333", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" },
  main: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 },
  summaryCard: { background: "#fff", borderRadius: 12, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", display: "flex", flexDirection: "column", justifyContent: "space-between" },
  summaryLabel: { fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  summaryValue: { fontSize: 26, fontWeight: 800 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  sectionTitle: { fontSize: 18, fontWeight: 700, margin: 0 },
  sectionCount: { background: "#EEF2FF", color: "#4F7FFA", borderRadius: 20, padding: "2px 10px", fontSize: 13, fontWeight: 600 },
  accountGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20, marginBottom: 40 },
  accountCard: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden", display: "flex", flexDirection: "column" },
  accountColorBar: { height: 5 },
  accountCardInner: { padding: "20px 22px" },
  accountTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  accountName: { fontSize: 16, fontWeight: 700, marginBottom: 3 },
  accountType: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },
  cardActions: { display: "flex", gap: 6, alignItems: "center" },
  editBtn: { background: "none", border: "none", fontSize: 15, cursor: "pointer", padding: 4, lineHeight: 1, opacity: 0.6 },
  deleteBtn: { background: "none", border: "none", color: "#ccc", fontSize: 16, cursor: "pointer", padding: 4, lineHeight: 1 },
  dueBadge: { fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 8, marginBottom: 12 },
  balanceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  balanceLabel: { fontSize: 13, color: "#888" },
  balanceValue: { fontSize: 22, fontWeight: 800, color: "#1a1a2e" },
  progressBg: { background: "#F0F0F5", borderRadius: 4, height: 6, marginBottom: 16, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 4, transition: "width 0.5s" },
  accountStats: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 },
  statItem: { background: "#F8F9FC", borderRadius: 8, padding: "8px 10px" },
  statLabel: { fontSize: 10, color: "#999", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 },
  statValue: { fontSize: 13, fontWeight: 700, color: "#1a1a2e" },
  detailsBtn: { width: "100%", background: "transparent", border: "1.5px solid", borderRadius: 8, padding: "9px", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "center" },
  emptyState: { textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyTitle: { fontSize: 18, fontWeight: 700, marginBottom: 8 },
  emptyText: { fontSize: 14, color: "#888", marginBottom: 20 },
  planSection: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: "28px", marginTop: 8 },
  planHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  planSubtitle: { fontSize: 13, color: "#888", marginTop: 4 },
  planControls: { display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F0F0F5" },
  planControlGroup: { display: "flex", flexDirection: "column", gap: 8 },
  planControlLabel: { fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  strategyTabs: { display: "flex", gap: 8 },
  strategyTab: { background: "#F3F4F6", border: "1.5px solid transparent", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#555" },
  strategyTabActive: { background: "#EEF2FF", borderColor: "#4F7FFA", color: "#4F7FFA" },
  extraInputRow: { display: "flex", alignItems: "center", gap: 6 },
  extraPrefix: { fontSize: 18, fontWeight: 700, color: "#4F7FFA" },
  extraInput: { border: "2px solid #4F7FFA", borderRadius: 8, padding: "7px 10px", fontSize: 16, fontWeight: 700, width: 90, color: "#1a1a2e", outline: "none", textAlign: "center" },
  extraSuffix: { fontSize: 13, color: "#888" },
  planRows: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 },
  planRow: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F8F9FC", borderRadius: 10, padding: "14px 16px", gap: 12 },
  planRowLeft: { display: "flex", alignItems: "center", gap: 12, flex: 1 },
  planRank: { width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 },
  planAccountName: { fontSize: 14, fontWeight: 600 },
  planAccountMeta: { fontSize: 12, color: "#999" },
  focusBadge: { background: "#EEF2FF", color: "#4F7FFA", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 },
  planRowRight: { display: "flex", alignItems: "center", gap: 28 },
  planPaymentBreakdown: { display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-end" },
  planPaymentItem: { display: "flex", gap: 8, alignItems: "center" },
  planPaymentLabel: { fontSize: 11, color: "#999", fontWeight: 600 },
  planPaymentValue: { fontSize: 13, fontWeight: 600, color: "#1a1a2e" },
  planTotal: { fontSize: 18, fontWeight: 800, textAlign: "right", minWidth: 90 },
  planTotalLabel: { fontSize: 10, color: "#999", fontWeight: 500, textAlign: "right" },
  planProgress: { display: "flex", alignItems: "center", gap: 10 },
  planProgressBg: { flex: 1, background: "#F0F0F5", borderRadius: 4, height: 8, overflow: "hidden", minWidth: 120 },
  planProgressFill: { height: "100%", background: "#10B981", borderRadius: 4, transition: "width 0.4s" },
  planProgressLabel: { fontSize: 13, fontWeight: 700, color: "#10B981", minWidth: 36 },
  paidBadge: { background: "#D1FAE5", color: "#065F46", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 },
  savingsBanner: { display: "flex", alignItems: "flex-start", gap: 12, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", marginBottom: 20 },
  savingsBannerIcon: { fontSize: 18, flexShrink: 0, marginTop: 1 },
  savingsBannerText: { fontSize: 13, color: "#166534", lineHeight: 1.6 },
  impactMsg: { fontSize: 12, color: "#555", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "8px 10px", marginTop: 10, lineHeight: 1.6 },
  planImpactMsg: { fontSize: 11, color: "#166534", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "4px 10px", whiteSpace: "nowrap" },
  markPaidBtn: { border: "none", borderRadius: 8, padding: "7px 14px", fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", marginLeft: 20 },
  planSummary: { background: "#F8F9FC", borderRadius: 10, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 },
  planSummaryItem: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  planSummaryLabel: { fontSize: 13, color: "#888" },
  planSummaryValue: { fontSize: 15, fontWeight: 700 },
  txnSummaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 },
  txnSummaryCard: { background: "#F8F9FC", borderRadius: 10, padding: "14px 16px" },
  txnSummaryLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  txnSummaryValue: { fontSize: 20, fontWeight: 800 },
  txnFilters: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #F0F0F5" },
  txnFilterGroup: { display: "flex", flexDirection: "column", gap: 6 },
  txnSelect: { border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "7px 12px", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" },
  txnAccountBadge: { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  modal: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #F0F0F5", flexShrink: 0 },
  modalTitle: { fontSize: 17, fontWeight: 700, margin: 0 },
  modalClose: { background: "none", border: "none", fontSize: 18, color: "#999", cursor: "pointer" },
  modalBody: { padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", flex: 1 },
  formGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 13, fontWeight: 600, color: "#444" },
  input: { border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "9px 12px", fontSize: 16, outline: "none", width: "100%", boxSizing: "border-box" },
  inputError: { borderColor: "#EF4444" },
  errorText: { fontSize: 12, color: "#EF4444" },
  modalFooter: { padding: "16px 24px", borderTop: "1px solid #F0F0F5", display: "flex", justifyContent: "flex-end", gap: 10 },
  cancelBtn: { background: "#F3F4F6", color: "#555", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
};
